import type { Language } from "../../types/i18n";

/*
 * Part of the appPages module (see ./appPages.ts), split out so one file stays
 * editable. English is the reference; the other languages must carry the same
 * keys, which the record type enforces.
 *
 * Strings of the public landing page (src/pages/LandingPage.tsx). Placeholders
 * in braces ({price}, {time}, {days}, {year}) are filled in by the page.
 */
const en = {
  // SEO
  miscLandSeoTitle:
    "Travel Health Passport – Your Medical Records and Emergency Card Abroad",
  miscLandSeoDescription:
    "HealthPass is a travel health passport: your conditions, medications, allergies, vaccinations and emergency contacts in one place, with an emergency card a doctor abroad reads in five languages. Free to start, no app store needed.",

  // Hero
  miscLandHeroLanguagesBadge: "Available in 5 languages",
  miscLandHeroTravelerHeadline:
    "Your travel health passport: medical records and an emergency card for every trip",
  miscLandHeroTravelerSubheadline:
    "HealthPass keeps your conditions, medications, allergies, vaccinations and emergency contacts in one place and puts the essentials on an emergency card. A doctor abroad scans its QR code and reads it in English, German, French, Spanish or Portuguese, with no app and no login. The card is free, and HealthPass runs in the browser, so there is nothing to download from an app store.",
  miscLandFaqTitle: "Questions travelers ask",
  miscLandFaqQ1: "What is a travel health passport?",
  miscLandFaqA1:
    "It keeps the medical information you need on a trip ready in one place: conditions, medications, allergies, vaccinations and emergency contacts, along with the health documents your destination asks for. HealthPass is one, and its emergency card can be read abroad without an app.",
  miscLandFaqQ2: "Can a doctor abroad read my medical information?",
  miscLandFaqA2:
    "Yes. Your emergency card has a QR code. Scanning it opens the card in a browser, in English, German, French, Spanish or Portuguese, with no app and no login. You decide what the card shows.",
  miscLandFaqQ3: "Do I need to download an app?",
  miscLandFaqA3:
    "No. HealthPass runs in the browser on any phone or computer, and you can add it to your home screen like an app. It is not in the App Store or Google Play.",
  miscLandFaqQ4: "What does HealthPass cost?",
  miscLandFaqA4:
    "The emergency card and basic trip planning are free for life. PLUS costs {plus} a month and PREMIUM {premium} a month; for a single trip, PLUS is {plusTrip} and PREMIUM {premiumTrip} for 90 days.",
  miscLandHeroNomadHeadline: "Health support for flexible and frequent travel",
  miscLandHeroNomadSubheadline:
    "Keep important health information, medication details, and emergency access ready while moving between destinations and time zones.",
  miscLandHeroCorporateHeadline:
    "Digital health support for business and work travel",
  miscLandHeroCorporateSubheadline:
    "Prepare important health information in one place for trips, check-in, and urgent situations during travel.",
  miscLandHeroFamilyHeadline:
    "Travel support for families, companions, and group travel",
  miscLandHeroFamilySubheadline:
    "Keep the most important medical details ready for children, older relatives, and shared travel planning.",
  miscLandHeroChronicHeadline:
    "Travel support for travelers with health issues",
  miscLandHeroChronicSubheadline:
    "Organize important medical information, medications, and emergency access before your trip so you can travel more confidently.",
  miscLandCtaCreateFreeAccount: "Create Your Free Account",
  miscLandCtaRequestDemo: "Request a Demo",
  miscLandFreeLifetimePlan: "FREE lifetime subscription plan",

  // Audiences
  miscLandAudienceHealthMinded: "Health-minded travelers",
  miscLandAudienceHealthIssues: "Travelers with health issues",
  miscLandAudienceOlder: "Older travelers",
  miscLandAudienceDisabled: "Disabled travelers",
  miscLandAudienceActivities: "Travelers planning special activities",
  miscLandAudiencePlaneCruise: "Travelers by plane or cruise ship",

  // Phone mockup
  miscLandMockEmergencyData: "Emergency Data",
  miscLandMockAlwaysAccessible: "Always accessible",
  miscLandMockScan: "Scan",
  miscLandMockBloodType: "Blood Type: A+",
  miscLandMockVerified: "Verified",
  miscLandMockAllergies: "Allergies: Penicillin",
  miscLandMockSevere: "Severe",
  miscLandMockUpcomingTrip: "Upcoming Trip",
  miscLandMockTripDestination: "Bangkok, Thailand",
  miscLandMockMedReminder: "Medication Reminder",
  miscLandMockDailyAt: "Daily at {time}",
  miscLandMockEncrypted: "Encrypted",

  // Trust badges
  miscLandTrustPrivacy: "Privacy-focused",
  miscLandTrustSecureAccess: "Secure digital access",
  miscLandTrustManySituations: "Useful for many travel situations",
  miscLandTrustInternational: "Designed for international travel",

  // HEALTHPASS Card showcase
  miscLandCardBadge: "HEALTHPASS Card",
  miscLandCardTitle: "One card. Four key travel actions.",
  miscLandCardIntro:
    "The HEALTHPASS Card keeps important health information ready for check-in, emergency communication, medical access, and your digital wallet.",
  miscLandCardPoint1:
    "Travel-ready digital identity and health access in one place",
  miscLandCardPoint2:
    "Fast sharing of important information when support is needed",
  miscLandCardPoint3:
    "Designed for international travel, cruise, and air travel",
  miscLandCardActionCheckIn: "Check-In",
  miscLandCardActionEmergency: "Emergency",
  miscLandCardActionMedAccess: "Med Access",
  miscLandCardActionWallet: "Wallet",

  // Chronic illness section
  miscLandChronicTitle: "Living with Chronic Illness?",
  miscLandChronicIntro:
    "Do not let health issues stop you from exploring the world. HEALTHPASS supports a wide range of chronic conditions and travel-related health needs.",
  miscLandChronicDiabetesTitle: "Diabetes (Type 1 & 2)",
  miscLandChronicDiabetes1: "Insulin and other medications in one list",
  miscLandChronicDiabetes2: "Blood sugar and lab values you log yourself",
  miscLandChronicDiabetes3: "Medication reminders",
  miscLandChronicDiabetes4: "Diabetes and allergies on the emergency card",
  miscLandChronicIbdTitle: "IBD (Crohn's & Colitis)",
  miscLandChronicIbd1: "Biologics and prescriptions in your medication list",
  miscLandChronicIbd2: "Doctor's letters stored as documents you can show",
  miscLandChronicIbd3: "Pharmacies and clinics near your destination",
  miscLandChronicIbd4: "Diagnosis and medication on the emergency card",
  miscLandChronicNeuroTitle: "Neurological (MS, Epilepsy)",
  miscLandChronicNeuro1: "Emergency card readable in five languages",
  miscLandChronicNeuro2: "Anticonvulsants and doses visible to responders",
  miscLandChronicNeuro3: "Emergency contacts a responder can call",
  miscLandChronicHospitalsNearby: "Hospitals and clinics near your destination",
  miscLandChronicCardiacTitle: "Cardiac Conditions",
  miscLandChronicCardiac1: "Pacemaker or ICD card stored as a document",
  miscLandChronicCardiac2:
    "Blood thinners in your medication list and reminders",
  miscLandChronicCardiac3: "Condition, allergies and contacts on the QR card",
  miscLandChronicAutoimmuneTitle: "Autoimmune (Rheumatoid, Lupus)",
  miscLandChronicAutoimmune1: "Immunosuppressants in your medication list",
  miscLandChronicAutoimmune2: "Vaccination record at hand when a doctor asks",
  miscLandChronicAutoimmune3: "Destination health requirements before you go",
  miscLandChronicAutoimmune4: "Clinics and pharmacies near your destination",
  miscLandChronicKidneyTitle: "Kidney Disease",
  miscLandChronicKidney1: "Dialysis history and lab values in your records",
  miscLandChronicKidney2: "Medications and doses in one list",
  miscLandChronicKidney3: "Kidney disease on the emergency card",
  miscLandConfidenceTitle: "Travel with confidence",
  miscLandConfidenceText:
    "You can travel without fear even if you are managing health issues. Find out how by using HEALTHPASS. You do not need to stay at home anymore.",
  miscLandConfidencePoint1: "Important health information ready when needed",
  miscLandConfidencePoint2: "Better preparation for different types of travel",
  miscLandConfidencePoint3:
    "Faster and clearer communication in a medical incident",
  miscLandConfidencePoint4: "A safer and more seamless travel experience",
  miscLandConfidenceQuote:
    "Travel can become possible again when the right information is available at the right moment.",
  miscLandStatsMillions: "Millions of people worldwide",
  miscLandStatsLiveWith: "live with chronic diseases.",
  miscLandStatsAvoid: "avoid travel out of fear.",
  miscLandStatsReconsider: "With HEALTHPASS, traveling can be reconsidered.",
  miscLandStartJourney: "Start your journey today",

  // Why HEALTHPASS matters
  miscLandWhyTitle: "Why HEALTHPASS matters",
  miscLandWhyIntro:
    "HEALTHPASS helps you understand health risks you may be exposed to during your trip and supports you in preparing for them.",
  miscLandWhyPrepTitle: "Better preparation before and during travel",
  miscLandWhyPrepText1:
    "HEALTHPASS helps you understand health risks that may be related to your destination, your type of travel, or your planned activities. It provides services that can help reduce these risks.",
  miscLandWhyPrepText2:
    "In case of a medical incident, the right information can be made available faster and more clearly, helping support a better and more appropriate treatment decision.",
  miscLandWhySeamlessTitle: "A more seamless digital travel experience",
  miscLandWhySeamlessText1:
    "Your interaction with tour operators or other travel partners who request health information becomes a more seamless digital experience.",
  miscLandWhySeamlessText2:
    "No paper forms, no re-entering the same information for every new trip, and less friction during check-in or border crossings.",
  miscLandWhyBanner:
    "It helps make travel safer and the overall travel experience more seamless.",

  // Travel support
  miscLandSupportTitle:
    "Travel by plane or cruise ship with better preparation",
  miscLandSupportIntro:
    "Keep key health information ready for check-in, border crossings, and urgent medical situations during travel.",
  miscLandSupportRiskTitle: "Risk awareness",
  miscLandSupportRiskText:
    "Understand relevant travel health risks before departure.",
  miscLandSupportFasterTitle: "Faster support",
  miscLandSupportFasterText:
    "Share key medical information more quickly when it matters.",
  miscLandSupportRepetitionTitle: "Less repetition",
  miscLandSupportRepetitionText:
    "Avoid re-entering the same health details for every trip.",
  miscLandSupportConfidenceTitle: "More confidence",
  miscLandSupportConfidenceText:
    "Travel with more peace of mind from departure to arrival.",
  miscLandDemoTitle: "Book a live product demo",
  miscLandDemoText:
    "See how HEALTHPASS can support safer and more seamless travel.",
  miscLandDemoCta: "Book a demo",

  // Travelers with health issues
  miscLandIssuesTitle: "For travelers with health issues",
  miscLandIssuesIntro:
    "HEALTHPASS can help you prepare important information before your trip so you can travel more confidently and with greater peace of mind.",
  miscLandIssuesFearTitle: "Travel without fear",
  miscLandIssuesFearSubtitle:
    "Better preparation for medical and travel-related situations",
  miscLandIssuesText1:
    "HEALTHPASS helps organize key information about chronic conditions, disabilities, medications, allergies, and other health considerations that may matter during a trip.",
  miscLandIssuesText2:
    "This can support faster communication and a more seamless digital experience when health information is requested.",
  miscLandIssuesHelpTitle: "What HEALTHPASS can help with:",
  miscLandIssuesHelp1: "Important information ready when needed",
  miscLandIssuesHelp2: "Better preparation for travel risks and activities",
  miscLandIssuesHelp3:
    "More seamless sharing during check-in or support requests",
  miscLandIssuesHelp4: "Greater confidence before and during travel",
  miscLandIssuesCta: "Start free, upgrade when you travel",
  miscLandIssuesCtaNote:
    "The free plan keeps your emergency card. PLUS adds destination health requirements and unlimited medication reminders for {price}/month.",

  // Features
  miscLandFeatureEmergencyTitle: "Emergency Health Data",
  miscLandFeatureEmergencyDesc:
    "Your critical health information available 24/7 via secure QR code",
  miscLandFeatureGdprTitle: "Built for the GDPR",
  miscLandFeatureGdprDesc:
    "Encrypted in transit and at rest, every access to a record logged",
  miscLandFeatureTravelTitle: "Travel Planning",
  miscLandFeatureTravelDesc:
    "Destination-specific health requirements and vaccination reminders",
  miscLandFeatureMedsTitle: "Medication Reminders",
  miscLandFeatureMedsDesc:
    "Never miss a dose with intelligent medication tracking",
  miscLandFeatureLanguagesTitle: "Five Languages",
  miscLandFeatureLanguagesDesc:
    "The app and the emergency view are available in English, German, French, Spanish and Portuguese.",
  miscLandFeatureMobileTitle: "Mobile Ready",
  miscLandFeatureMobileDesc:
    "Access your health data anytime, anywhere on any device",

  // How it works
  miscLandStep1Title: "Create Account",
  miscLandStep1Desc: "Sign up in 2 minutes with just your email",
  miscLandStep2Title: "Add Health Data",
  miscLandStep2Desc: "Enter your allergies, medications, and conditions",
  miscLandStep3Title: "Travel Safely",
  miscLandStep3Desc: "Get your QR code and travel with peace of mind",

  // Conditions hub
  miscLandHubTitle: "Traveling with a Chronic Condition?",
  miscLandHubIntro:
    "HEALTHPASS supports a wide range of chronic conditions with travel-ready information, multilingual support, and emergency access tools. You do not have to stay home.",
  miscLandHubDiabetes1: "Insulin & medication list",
  miscLandHubDiabetes2: "Blood sugar log",
  miscLandHubDiabetes3: "Reminders per dose",
  miscLandHubHeartTitle: "Heart Disease",
  miscLandHubHeart1: "Pacemaker docs",
  miscLandHubHeart2: "Blood thinner reminders",
  miscLandHubConditionOnQr: "Condition on the QR card",
  miscLandHubCopdTitle: "COPD & Asthma",
  miscLandHubCopd1: "Oxygen forms as documents",
  miscLandHubCopd2: "Inhaler reminders",
  miscLandHubCopd3: "Pharmacies at your destination",
  miscLandHubCrohnsTitle: "Crohn's & Colitis",
  miscLandHubCrohns1: "Biologics in your med list",
  miscLandHubCrohns2: "Letters stored as documents",
  miscLandHubClinics: "Clinics at your destination",
  miscLandHubEpilepsyTitle: "Epilepsy & Seizures",
  miscLandHubEpilepsy1: "Emergency card in 5 languages",
  miscLandHubEpilepsy2: "Anticonvulsants & doses",
  miscLandHubHospitals: "Hospitals at your destination",
  miscLandHubKidney1: "Dialysis history & labs",
  miscLandHubKidney2: "Medications & doses",
  miscLandHubRaTitle: "Rheumatoid Arthritis",
  miscLandHubRa1: "Immunosuppressant list",
  miscLandHubRa2: "Vaccination record",
  miscLandHubMsTitle: "Multiple Sclerosis",
  miscLandHubMs1: "Medications & infusions",
  miscLandHubReadGuide: "Read travel guide",
  miscLandHubMoreGuides: "More travel health guides (in English)",
  miscLandHubCta: "Start with the FREE lifetime plan",
  miscLandHubNote:
    "No credit card required · Works with your existing healthcare provider",

  // Use cases
  miscLandCasesTitle: "Example travel situations",
  miscLandCasesIntro:
    "Illustrative examples of how HEALTHPASS can support safer and more seamless travel.",
  miscLandChallenge: "Challenge:",
  miscLandSolution: "Solution:",
  miscLandCaseAllReturned: "All 22 returned safely",
  miscLandCaseGroupTitle: "22 travelers, 22 QR codes",
  miscLandCaseGroupSubtitle: "Senior group round trip in Portugal",
  miscLandCaseGroupChallenge:
    "Inge (72, pacemaker), Werner (75, hip surgery), Helga (68, dialysis) - 22 seniors, 110 medications, 10 days in Portugal.",
  miscLandCaseGroupSolution:
    "Every traveler carried their own QR code, so a paramedic could read medications and conditions without waiting for a relative to be reached.",
  miscLandCaseGroupResult2: "Conditions readable without a translator",
  miscLandCaseGroupResult3: "No relative needed to confirm a medication",
  miscLandCaseCruiseTitle: "Cruise with Confidence",
  miscLandCaseCruiseSubtitle: "Maria's Mediterranean Journey",
  miscLandCaseCruiseChallenge:
    "Maria (62) has diabetes, penicillin allergy, and a pacemaker. 14-day cruise through Greece, Italy, and Croatia.",
  miscLandCaseCruiseSolution:
    "QR code armband with her health data in five languages, medication reminders, instant access for ship doctors.",
  miscLandCaseCruiseResult1: "Zero missed medications",
  miscLandCaseCruiseResult2: "No hunt for a medication list in a foreign port",
  miscLandCaseCruiseResult3:
    "Ship doctor sees her pacemaker and allergy at once",
  miscLandCaseFamilyTitle: "Family Travel Made Safe",
  miscLandCaseFamilySubtitle: "The Müller Family in Thailand",
  miscLandCaseFamilyChallenge:
    "Family of 4 with Emma (8) having life-threatening peanut allergy. 3 weeks in Thailand & Cambodia.",
  miscLandCaseFamilySolution:
    "A QR code for Emma naming the allergy and her adrenaline auto-injector, with her parents as the emergency contacts who are mailed when it is read.",
  miscLandCaseFamilyResult1: "Important information available quickly",
  miscLandCaseFamilyResult2: "The allergy is stated before anyone asks",
  miscLandCaseFamilyResult3: "Parents are mailed when the code is read",
  miscLandCaseSoloTitle: "Solo Travel, Not Alone",
  miscLandCaseSoloSubtitle: "Max's 6-Month World Tour",
  miscLandCaseSoloChallenge:
    "Max (26) with Type-1 Diabetes traveling solo across 12 countries. Insulin management, extreme activities, remote areas.",
  miscLandCaseSoloSolution:
    "Health data readable offline, a printed QR code on the backpack, his insulin in the medication list, and home notified when the code is read.",
  miscLandCaseSoloResult1: "Emergency information available offline",
  miscLandCaseSoloResult2: "Insulin and doses legible to a stranger",
  miscLandCaseSoloResult3: "Diabetes named before treatment starts",
  miscLandCaseSeniorsTitle: "Group Travel Simplified",
  miscLandCaseSeniorsSubtitle: "Seniors Group in Portugal",
  miscLandCaseSeniorsChallenge:
    "22 seniors (60-78 years) with 110 medications total. Pacemakers, dialysis, stroke history, hearing loss.",
  miscLandCaseSeniorsSolution:
    "A QR code per traveler, each with medications, conditions and emergency contacts, and the hospitals and clinics near each stop.",
  miscLandCaseSeniorsResult2: "110 medications documented, not remembered",
  miscLandCaseSeniorsResult3: "Each traveler's contacts reachable on the spot",
  miscLandCasesMore: "See more travel use cases",

  // Pricing
  miscLandPricingMonthlyTitle: "Monthly Subscriptions",
  miscLandPricingMonthlyNote:
    "Includes a FREE lifetime subscription plan. Upgrade anytime.",
  miscLandPeriodForever: "forever",
  miscLandPeriodMonth: "month",
  miscLandPeriodOneTrip: "one trip",
  miscLandSingleAccess: "{days} days access",
  miscLandPlanEmergencyData: "Emergency health data",
  miscLandPlanQrAccess: "QR code access",
  miscLandPlanBasicTravel: "Basic travel planning",
  miscLandPlanFiveReminders: "5 medication reminders/month",
  miscLandPlanGdprStorage: "GDPR-compliant storage",
  miscLandPlanEverythingFree: "Everything in FREE",
  miscLandPlanUnlimitedReminders: "Unlimited reminders",
  miscLandPlanTravelReqs: "Travel health requirements",
  miscLandPlanVaccTracking: "Vaccination tracking",
  miscLandPlanStorage1: "Document storage (1 GB)",
  miscLandPlanPriority: "Priority support",
  miscLandPlanOffline: "Offline access",
  miscLandPlanEverythingPlus: "Everything in PLUS",
  miscLandPlanCompanions: "Travel companions on a trip",
  miscLandPlanNearby: "Hospitals, pharmacies and clinics near your destination",
  miscLandPlanDoctorSummary: "Trip health summary sent to your own doctor",
  miscLandPlanAdviceWatch:
    "Watch on changing health advice for your destinations",
  miscLandPlanStorage10: "Document storage (10 GB)",
  miscLandPlanAllPlus: "All PLUS features",
  miscLandPlanAllPremium: "All PREMIUM features",
  miscLandChoosePlus: "Choose PLUS",
  miscLandChoosePremium: "Choose PREMIUM",
  miscLandBuySingleTrip: "Buy Single Trip",
  miscLandBadgeMostPopular: "Most Popular",
  miscLandBadgeBestOneTrip: "Best for one trip",
  miscLandSingleTitle: "Single Trip Plans",
  miscLandSingleNote:
    "One-time payment. Full access for 90 days — no subscription needed.",

  // Final CTA
  miscLandSignIn: "Sign In",
  miscLandCtaNote:
    "No credit card required • FREE lifetime subscription plan • Upgrade anytime",

  // Footer
  miscLandLogoAlt: "HealthPass logo",
  miscLandFooterTagline:
    "Your health data, always with you. Travel safely worldwide.",
  miscLandFooterProduct: "Product",
  miscLandFooterFeatures: "Features",
  miscLandFooterPricing: "Pricing",
  miscLandFooterSecurity: "Security",
  miscLandFooterFaq: "FAQ",
  miscLandFooterRecordsApp: "Travel medical records app",
  miscLandFooterCompany: "Company",
  miscLandFooterBlog: "Blog",
  miscLandFooterContact: "Contact",
  miscLandFooterLegal: "Legal",
  miscLandFooterPrivacy: "Privacy Policy",
  miscLandFooterTerms: "Terms of Service",
  miscLandFooterImprint: "Imprint",
  miscLandFooterCopyright:
    "© {year} HealthPass. All rights reserved. Made for travelers.",
};

export type AppPagesLandingStrings = typeof en;

const de: AppPagesLandingStrings = {
  miscLandSeoTitle:
    "Reisegesundheitspass – Ihre Krankenakte und Notfallkarte auf Reisen",
  miscLandSeoDescription:
    "HealthPass ist ein Reisegesundheitspass: Erkrankungen, Medikamente, Allergien, Impfungen und Notfallkontakte an einem Ort, mit einer Notfallkarte, die ein Arzt im Ausland in fünf Sprachen liest. Kostenlos starten, ohne App Store.",

  miscLandHeroLanguagesBadge: "In 5 Sprachen verfügbar",
  miscLandHeroTravelerHeadline:
    "Ihr Reisegesundheitspass: Krankenakte und Notfallkarte für jede Reise",
  miscLandHeroTravelerSubheadline:
    "HealthPass bewahrt Ihre Erkrankungen, Medikamente, Allergien, Impfungen und Notfallkontakte an einem Ort auf und fasst das Wichtigste auf einer Notfallkarte zusammen. Ein Arzt im Ausland scannt ihren QR-Code und liest sie auf Englisch, Deutsch, Französisch, Spanisch oder Portugiesisch, ohne App und ohne Anmeldung. Die Karte ist kostenlos, und HealthPass läuft im Browser – es gibt nichts aus einem App Store herunterzuladen.",
  miscLandFaqTitle: "Fragen, die Reisende stellen",
  miscLandFaqQ1: "Was ist ein Reisegesundheitspass?",
  miscLandFaqA1:
    "Er hält die medizinischen Informationen bereit, die Sie auf Reisen brauchen: Erkrankungen, Medikamente, Allergien, Impfungen und Notfallkontakte, dazu die Gesundheitsdokumente, die Ihr Reiseziel verlangt. HealthPass ist einer, und seine Notfallkarte lässt sich im Ausland ohne App lesen.",
  miscLandFaqQ2: "Kann ein Arzt im Ausland meine medizinischen Daten lesen?",
  miscLandFaqA2:
    "Ja. Ihre Notfallkarte hat einen QR-Code. Wer ihn scannt, öffnet die Karte im Browser, auf Englisch, Deutsch, Französisch, Spanisch oder Portugiesisch, ohne App und ohne Anmeldung. Sie entscheiden, was die Karte zeigt.",
  miscLandFaqQ3: "Muss ich eine App herunterladen?",
  miscLandFaqA3:
    "Nein. HealthPass läuft im Browser auf jedem Smartphone und Computer und lässt sich wie eine App auf den Startbildschirm legen. Im App Store oder bei Google Play gibt es HealthPass nicht.",
  miscLandFaqQ4: "Was kostet HealthPass?",
  miscLandFaqA4:
    "Die Notfallkarte und die einfache Reiseplanung sind dauerhaft kostenlos. PLUS kostet {plus} im Monat und PREMIUM {premium} im Monat; für eine einzelne Reise kostet PLUS {plusTrip} und PREMIUM {premiumTrip} für 90 Tage.",
  miscLandHeroNomadHeadline:
    "Gesundheitliche Unterstützung für flexibles und häufiges Reisen",
  miscLandHeroNomadSubheadline:
    "Halten Sie wichtige Gesundheitsinformationen, Medikamentenangaben und den Notfallzugang bereit, während Sie zwischen Reisezielen und Zeitzonen unterwegs sind.",
  miscLandHeroCorporateHeadline:
    "Digitale Gesundheitsunterstützung für Geschäfts- und Dienstreisen",
  miscLandHeroCorporateSubheadline:
    "Bereiten Sie wichtige Gesundheitsinformationen an einem Ort vor – für Reisen, den Check-in und dringende Situationen unterwegs.",
  miscLandHeroFamilyHeadline:
    "Reiseunterstützung für Familien, Begleitpersonen und Gruppenreisen",
  miscLandHeroFamilySubheadline:
    "Halten Sie die wichtigsten medizinischen Angaben für Kinder, ältere Angehörige und die gemeinsame Reiseplanung bereit.",
  miscLandHeroChronicHeadline:
    "Reiseunterstützung für Reisende mit gesundheitlichen Einschränkungen",
  miscLandHeroChronicSubheadline:
    "Ordnen Sie wichtige medizinische Informationen, Medikamente und den Notfallzugang vor Ihrer Reise, damit Sie sicherer reisen können.",
  miscLandCtaCreateFreeAccount: "Kostenloses Konto erstellen",
  miscLandCtaRequestDemo: "Demo anfragen",
  miscLandFreeLifetimePlan: "Dauerhaft KOSTENLOSER Tarif",

  miscLandAudienceHealthMinded: "Gesundheitsbewusste Reisende",
  miscLandAudienceHealthIssues: "Reisende mit gesundheitlichen Einschränkungen",
  miscLandAudienceOlder: "Ältere Reisende",
  miscLandAudienceDisabled: "Reisende mit Behinderung",
  miscLandAudienceActivities: "Reisende mit besonderen Aktivitäten",
  miscLandAudiencePlaneCruise: "Reisende per Flugzeug oder Kreuzfahrtschiff",

  miscLandMockEmergencyData: "Notfalldaten",
  miscLandMockAlwaysAccessible: "Immer verfügbar",
  miscLandMockScan: "Scannen",
  miscLandMockBloodType: "Blutgruppe: A+",
  miscLandMockVerified: "Bestätigt",
  miscLandMockAllergies: "Allergien: Penicillin",
  miscLandMockSevere: "Schwer",
  miscLandMockUpcomingTrip: "Nächste Reise",
  miscLandMockTripDestination: "Bangkok, Thailand",
  miscLandMockMedReminder: "Medikamentenerinnerung",
  miscLandMockDailyAt: "Täglich um {time}",
  miscLandMockEncrypted: "Verschlüsselt",

  miscLandTrustPrivacy: "Datenschutzorientiert",
  miscLandTrustSecureAccess: "Sicherer digitaler Zugang",
  miscLandTrustManySituations: "Nützlich in vielen Reisesituationen",
  miscLandTrustInternational: "Für internationale Reisen entwickelt",

  miscLandCardBadge: "HEALTHPASS-Karte",
  miscLandCardTitle: "Eine Karte. Vier zentrale Reisefunktionen.",
  miscLandCardIntro:
    "Die HEALTHPASS-Karte hält wichtige Gesundheitsinformationen bereit – für den Check-in, die Kommunikation im Notfall, den medizinischen Zugriff und Ihr digitales Wallet.",
  miscLandCardPoint1:
    "Reisebereite digitale Identität und Gesundheitszugang an einem Ort",
  miscLandCardPoint2:
    "Schnelles Teilen wichtiger Informationen, wenn Hilfe gebraucht wird",
  miscLandCardPoint3: "Für internationale Reisen, Kreuzfahrten und Flugreisen",
  miscLandCardActionCheckIn: "Check-in",
  miscLandCardActionEmergency: "Notfall",
  miscLandCardActionMedAccess: "Med. Zugriff",
  miscLandCardActionWallet: "Wallet",

  miscLandChronicTitle: "Leben Sie mit einer chronischen Erkrankung?",
  miscLandChronicIntro:
    "Lassen Sie sich von gesundheitlichen Problemen nicht davon abhalten, die Welt zu entdecken. HEALTHPASS unterstützt eine Vielzahl chronischer Erkrankungen und reisebezogener Gesundheitsbedürfnisse.",
  miscLandChronicDiabetesTitle: "Diabetes (Typ 1 & 2)",
  miscLandChronicDiabetes1: "Insulin und andere Medikamente in einer Liste",
  miscLandChronicDiabetes2:
    "Blutzucker- und Laborwerte, die Sie selbst erfassen",
  miscLandChronicDiabetes3: "Medikamentenerinnerungen",
  miscLandChronicDiabetes4: "Diabetes und Allergien auf dem Notfallausweis",
  miscLandChronicIbdTitle: "CED (Morbus Crohn & Colitis)",
  miscLandChronicIbd1: "Biologika und Rezepte in Ihrer Medikamentenliste",
  miscLandChronicIbd2: "Arztbriefe als Dokumente, die Sie vorzeigen können",
  miscLandChronicIbd3: "Apotheken und Kliniken in der Nähe Ihres Reiseziels",
  miscLandChronicIbd4: "Diagnose und Medikation auf dem Notfallausweis",
  miscLandChronicNeuroTitle: "Neurologisch (MS, Epilepsie)",
  miscLandChronicNeuro1: "Notfallausweis in fünf Sprachen lesbar",
  miscLandChronicNeuro2:
    "Antiepileptika und Dosierungen für Ersthelfer sichtbar",
  miscLandChronicNeuro3: "Notfallkontakte, die Ersthelfer anrufen können",
  miscLandChronicHospitalsNearby:
    "Krankenhäuser und Kliniken in der Nähe Ihres Reiseziels",
  miscLandChronicCardiacTitle: "Herzerkrankungen",
  miscLandChronicCardiac1:
    "Herzschrittmacher- oder ICD-Ausweis als Dokument gespeichert",
  miscLandChronicCardiac2:
    "Blutverdünner in Ihrer Medikamentenliste und Erinnerungen",
  miscLandChronicCardiac3:
    "Erkrankung, Allergien und Kontakte auf der QR-Karte",
  miscLandChronicAutoimmuneTitle: "Autoimmun (Rheuma, Lupus)",
  miscLandChronicAutoimmune1: "Immunsuppressiva in Ihrer Medikamentenliste",
  miscLandChronicAutoimmune2:
    "Impfnachweis griffbereit, wenn eine Ärztin oder ein Arzt fragt",
  miscLandChronicAutoimmune3:
    "Gesundheitsanforderungen des Reiseziels vor der Abreise",
  miscLandChronicAutoimmune4:
    "Kliniken und Apotheken in der Nähe Ihres Reiseziels",
  miscLandChronicKidneyTitle: "Nierenerkrankung",
  miscLandChronicKidney1: "Dialyseverlauf und Laborwerte in Ihren Unterlagen",
  miscLandChronicKidney2: "Medikamente und Dosierungen in einer Liste",
  miscLandChronicKidney3: "Nierenerkrankung auf dem Notfallausweis",
  miscLandConfidenceTitle: "Reisen Sie mit Zuversicht",
  miscLandConfidenceText:
    "Sie können ohne Angst reisen, auch wenn Sie mit gesundheitlichen Einschränkungen leben. Erfahren Sie mit HEALTHPASS, wie das geht. Sie müssen nicht länger zu Hause bleiben.",
  miscLandConfidencePoint1:
    "Wichtige Gesundheitsinformationen bereit, wenn sie gebraucht werden",
  miscLandConfidencePoint2: "Bessere Vorbereitung auf verschiedene Reisearten",
  miscLandConfidencePoint3:
    "Schnellere und klarere Kommunikation bei einem medizinischen Vorfall",
  miscLandConfidencePoint4: "Ein sichereres und reibungsloseres Reiseerlebnis",
  miscLandConfidenceQuote:
    "Reisen kann wieder möglich werden, wenn die richtigen Informationen im richtigen Moment verfügbar sind.",
  miscLandStatsMillions: "Millionen Menschen weltweit",
  miscLandStatsLiveWith: "leben mit chronischen Erkrankungen.",
  miscLandStatsAvoid: "verzichten aus Angst auf Reisen.",
  miscLandStatsReconsider: "Mit HEALTHPASS lässt sich das Reisen neu denken.",
  miscLandStartJourney: "Starten Sie noch heute",

  miscLandWhyTitle: "Warum HEALTHPASS wichtig ist",
  miscLandWhyIntro:
    "HEALTHPASS hilft Ihnen, Gesundheitsrisiken zu verstehen, denen Sie auf Ihrer Reise ausgesetzt sein können, und unterstützt Sie bei der Vorbereitung darauf.",
  miscLandWhyPrepTitle: "Bessere Vorbereitung vor und während der Reise",
  miscLandWhyPrepText1:
    "HEALTHPASS hilft Ihnen, Gesundheitsrisiken zu verstehen, die mit Ihrem Reiseziel, Ihrer Reiseart oder Ihren geplanten Aktivitäten zusammenhängen können. Es bietet Leistungen, die helfen können, diese Risiken zu verringern.",
  miscLandWhyPrepText2:
    "Bei einem medizinischen Vorfall können die richtigen Informationen schneller und klarer bereitgestellt werden – das unterstützt eine bessere und angemessenere Behandlungsentscheidung.",
  miscLandWhySeamlessTitle: "Ein reibungsloseres digitales Reiseerlebnis",
  miscLandWhySeamlessText1:
    "Der Austausch mit Reiseveranstaltern oder anderen Reisepartnern, die Gesundheitsinformationen anfordern, wird zu einem reibungsloseren digitalen Erlebnis.",
  miscLandWhySeamlessText2:
    "Keine Papierformulare, keine erneute Eingabe derselben Informationen für jede neue Reise und weniger Aufwand beim Check-in oder an Grenzübergängen.",
  miscLandWhyBanner:
    "So wird Reisen sicherer und das gesamte Reiseerlebnis reibungsloser.",

  miscLandSupportTitle:
    "Besser vorbereitet mit dem Flugzeug oder Kreuzfahrtschiff reisen",
  miscLandSupportIntro:
    "Halten Sie wichtige Gesundheitsinformationen für den Check-in, Grenzübergänge und dringende medizinische Situationen auf Reisen bereit.",
  miscLandSupportRiskTitle: "Risikobewusstsein",
  miscLandSupportRiskText:
    "Relevante gesundheitliche Reiserisiken vor der Abreise verstehen.",
  miscLandSupportFasterTitle: "Schnellere Hilfe",
  miscLandSupportFasterText:
    "Wichtige medizinische Informationen schneller teilen, wenn es darauf ankommt.",
  miscLandSupportRepetitionTitle: "Weniger Wiederholung",
  miscLandSupportRepetitionText:
    "Dieselben Gesundheitsangaben nicht für jede Reise neu eingeben.",
  miscLandSupportConfidenceTitle: "Mehr Sicherheit",
  miscLandSupportConfidenceText:
    "Von der Abreise bis zur Ankunft beruhigter reisen.",
  miscLandDemoTitle: "Buchen Sie eine Live-Produktdemo",
  miscLandDemoText:
    "Erleben Sie, wie HEALTHPASS sichereres und reibungsloseres Reisen unterstützt.",
  miscLandDemoCta: "Demo buchen",

  miscLandIssuesTitle: "Für Reisende mit gesundheitlichen Einschränkungen",
  miscLandIssuesIntro:
    "HEALTHPASS hilft Ihnen, wichtige Informationen vor Ihrer Reise vorzubereiten, damit Sie zuversichtlicher und beruhigter reisen können.",
  miscLandIssuesFearTitle: "Reisen ohne Angst",
  miscLandIssuesFearSubtitle:
    "Bessere Vorbereitung auf medizinische und reisebezogene Situationen",
  miscLandIssuesText1:
    "HEALTHPASS hilft, wichtige Informationen zu chronischen Erkrankungen, Behinderungen, Medikamenten, Allergien und anderen gesundheitlichen Aspekten zu ordnen, die auf einer Reise wichtig sein können.",
  miscLandIssuesText2:
    "Das unterstützt eine schnellere Kommunikation und ein reibungsloseres digitales Erlebnis, wenn Gesundheitsinformationen angefordert werden.",
  miscLandIssuesHelpTitle: "Wobei HEALTHPASS helfen kann:",
  miscLandIssuesHelp1:
    "Wichtige Informationen bereit, wenn sie gebraucht werden",
  miscLandIssuesHelp2: "Bessere Vorbereitung auf Reiserisiken und Aktivitäten",
  miscLandIssuesHelp3:
    "Reibungsloseres Teilen beim Check-in oder bei Hilfeanfragen",
  miscLandIssuesHelp4: "Mehr Zuversicht vor und während der Reise",
  miscLandIssuesCta: "Kostenlos starten, upgraden, wenn Sie reisen",
  miscLandIssuesCtaNote:
    "Der kostenlose Tarif behält Ihren Notfallausweis. PLUS ergänzt die Gesundheitsanforderungen des Reiseziels und unbegrenzte Medikamentenerinnerungen für {price}/Monat.",

  miscLandFeatureEmergencyTitle: "Notfall-Gesundheitsdaten",
  miscLandFeatureEmergencyDesc:
    "Ihre wichtigsten Gesundheitsinformationen rund um die Uhr per sicherem QR-Code verfügbar",
  miscLandFeatureGdprTitle: "Für die DSGVO gebaut",
  miscLandFeatureGdprDesc:
    "Verschlüsselt bei der Übertragung und im Ruhezustand, jeder Zugriff auf einen Datensatz protokolliert",
  miscLandFeatureTravelTitle: "Reiseplanung",
  miscLandFeatureTravelDesc:
    "Reiseziel-spezifische Gesundheitsanforderungen und Impferinnerungen",
  miscLandFeatureMedsTitle: "Medikamentenerinnerungen",
  miscLandFeatureMedsDesc:
    "Keine Dosis mehr verpassen dank intelligenter Medikamentenverfolgung",
  miscLandFeatureLanguagesTitle: "Fünf Sprachen",
  miscLandFeatureLanguagesDesc:
    "Die App und die Notfallansicht sind auf Englisch, Deutsch, Französisch, Spanisch und Portugiesisch verfügbar.",
  miscLandFeatureMobileTitle: "Mobil einsatzbereit",
  miscLandFeatureMobileDesc:
    "Greifen Sie jederzeit und überall auf jedem Gerät auf Ihre Gesundheitsdaten zu",

  miscLandStep1Title: "Konto erstellen",
  miscLandStep1Desc: "In 2 Minuten nur mit Ihrer E-Mail-Adresse registrieren",
  miscLandStep2Title: "Gesundheitsdaten hinzufügen",
  miscLandStep2Desc: "Erfassen Sie Allergien, Medikamente und Erkrankungen",
  miscLandStep3Title: "Sicher reisen",
  miscLandStep3Desc: "Holen Sie sich Ihren QR-Code und reisen Sie beruhigt",

  miscLandHubTitle: "Mit einer chronischen Erkrankung auf Reisen?",
  miscLandHubIntro:
    "HEALTHPASS unterstützt eine Vielzahl chronischer Erkrankungen mit reisebereiten Informationen, mehrsprachiger Unterstützung und Werkzeugen für den Notfallzugang. Sie müssen nicht zu Hause bleiben.",
  miscLandHubDiabetes1: "Insulin- & Medikamentenliste",
  miscLandHubDiabetes2: "Blutzuckertagebuch",
  miscLandHubDiabetes3: "Erinnerung für jede Dosis",
  miscLandHubHeartTitle: "Herzerkrankung",
  miscLandHubHeart1: "Herzschrittmacher-Unterlagen",
  miscLandHubHeart2: "Erinnerungen für Blutverdünner",
  miscLandHubConditionOnQr: "Erkrankung auf der QR-Karte",
  miscLandHubCopdTitle: "COPD & Asthma",
  miscLandHubCopd1: "Sauerstoff-Formulare als Dokumente",
  miscLandHubCopd2: "Erinnerungen für Inhalatoren",
  miscLandHubCopd3: "Apotheken am Reiseziel",
  miscLandHubCrohnsTitle: "Morbus Crohn & Colitis",
  miscLandHubCrohns1: "Biologika in Ihrer Medikamentenliste",
  miscLandHubCrohns2: "Arztbriefe als Dokumente",
  miscLandHubClinics: "Kliniken am Reiseziel",
  miscLandHubEpilepsyTitle: "Epilepsie & Krampfanfälle",
  miscLandHubEpilepsy1: "Notfallausweis in 5 Sprachen",
  miscLandHubEpilepsy2: "Antiepileptika & Dosierungen",
  miscLandHubHospitals: "Krankenhäuser am Reiseziel",
  miscLandHubKidney1: "Dialyseverlauf & Laborwerte",
  miscLandHubKidney2: "Medikamente & Dosierungen",
  miscLandHubRaTitle: "Rheumatoide Arthritis",
  miscLandHubRa1: "Liste der Immunsuppressiva",
  miscLandHubRa2: "Impfnachweis",
  miscLandHubMsTitle: "Multiple Sklerose",
  miscLandHubMs1: "Medikamente & Infusionen",
  miscLandHubReadGuide: "Reiseratgeber lesen",
  miscLandHubMoreGuides: "Weitere Reisegesundheits-Ratgeber (auf Englisch)",
  miscLandHubCta: "Mit dem dauerhaft KOSTENLOSEN Tarif starten",
  miscLandHubNote:
    "Keine Kreditkarte erforderlich · Funktioniert mit Ihrer bestehenden ärztlichen Versorgung",

  miscLandCasesTitle: "Beispielhafte Reisesituationen",
  miscLandCasesIntro:
    "Beispiele dafür, wie HEALTHPASS sichereres und reibungsloseres Reisen unterstützen kann.",
  miscLandChallenge: "Herausforderung:",
  miscLandSolution: "Lösung:",
  miscLandCaseAllReturned: "Alle 22 sind wohlbehalten zurückgekehrt",
  miscLandCaseGroupTitle: "22 Reisende, 22 QR-Codes",
  miscLandCaseGroupSubtitle: "Seniorengruppe auf Rundreise in Portugal",
  miscLandCaseGroupChallenge:
    "Inge (72, Herzschrittmacher), Werner (75, Hüftoperation), Helga (68, Dialyse) – 22 Seniorinnen und Senioren, 110 Medikamente, 10 Tage in Portugal.",
  miscLandCaseGroupSolution:
    "Alle Reisenden trugen ihren eigenen QR-Code bei sich, sodass ein Rettungssanitäter Medikamente und Erkrankungen lesen konnte, ohne darauf zu warten, dass Angehörige erreicht werden.",
  miscLandCaseGroupResult2: "Erkrankungen ohne Dolmetscher lesbar",
  miscLandCaseGroupResult3:
    "Keine Angehörigen nötig, um ein Medikament zu bestätigen",
  miscLandCaseCruiseTitle: "Kreuzfahrt mit Zuversicht",
  miscLandCaseCruiseSubtitle: "Marias Mittelmeerreise",
  miscLandCaseCruiseChallenge:
    "Maria (62) hat Diabetes, eine Penicillinallergie und einen Herzschrittmacher. 14-tägige Kreuzfahrt durch Griechenland, Italien und Kroatien.",
  miscLandCaseCruiseSolution:
    "QR-Code-Armband mit ihren Gesundheitsdaten in fünf Sprachen, Medikamentenerinnerungen und sofortigem Zugriff für die Schiffsärzte.",
  miscLandCaseCruiseResult1: "Keine einzige vergessene Medikamenteneinnahme",
  miscLandCaseCruiseResult2:
    "Keine Suche nach einer Medikamentenliste in einem fremden Hafen",
  miscLandCaseCruiseResult3:
    "Der Schiffsarzt sieht Herzschrittmacher und Allergie sofort",
  miscLandCaseFamilyTitle: "Sicher reisen als Familie",
  miscLandCaseFamilySubtitle: "Familie Müller in Thailand",
  miscLandCaseFamilyChallenge:
    "Vierköpfige Familie, Emma (8) hat eine lebensbedrohliche Erdnussallergie. 3 Wochen in Thailand & Kambodscha.",
  miscLandCaseFamilySolution:
    "Ein QR-Code für Emma, der die Allergie und ihren Adrenalin-Autoinjektor nennt, mit ihren Eltern als Notfallkontakten, die beim Auslesen per E-Mail benachrichtigt werden.",
  miscLandCaseFamilyResult1: "Wichtige Informationen schnell verfügbar",
  miscLandCaseFamilyResult2: "Die Allergie ist genannt, bevor jemand fragt",
  miscLandCaseFamilyResult3:
    "Die Eltern erhalten eine E-Mail, wenn der Code gelesen wird",
  miscLandCaseSoloTitle: "Allein reisen, aber nicht allein",
  miscLandCaseSoloSubtitle: "Max' 6-monatige Weltreise",
  miscLandCaseSoloChallenge:
    "Max (26) mit Typ-1-Diabetes reist allein durch 12 Länder. Insulinmanagement, Extremaktivitäten, abgelegene Regionen.",
  miscLandCaseSoloSolution:
    "Gesundheitsdaten offline lesbar, ein gedruckter QR-Code am Rucksack, sein Insulin in der Medikamentenliste und eine Benachrichtigung nach Hause, wenn der Code gelesen wird.",
  miscLandCaseSoloResult1: "Notfallinformationen offline verfügbar",
  miscLandCaseSoloResult2: "Insulin und Dosierungen für Fremde lesbar",
  miscLandCaseSoloResult3: "Diabetes ist vor Behandlungsbeginn bekannt",
  miscLandCaseSeniorsTitle: "Gruppenreisen leicht gemacht",
  miscLandCaseSeniorsSubtitle: "Seniorengruppe in Portugal",
  miscLandCaseSeniorsChallenge:
    "22 Seniorinnen und Senioren (60–78 Jahre) mit insgesamt 110 Medikamenten. Herzschrittmacher, Dialyse, Schlaganfall in der Vorgeschichte, Hörverlust.",
  miscLandCaseSeniorsSolution:
    "Ein QR-Code pro Person, jeweils mit Medikamenten, Erkrankungen und Notfallkontakten, dazu die Krankenhäuser und Kliniken an jeder Station.",
  miscLandCaseSeniorsResult2: "110 Medikamente dokumentiert statt erinnert",
  miscLandCaseSeniorsResult3:
    "Die Kontakte jeder reisenden Person sofort erreichbar",
  miscLandCasesMore: "Weitere Anwendungsbeispiele ansehen",

  miscLandPricingMonthlyTitle: "Monatliche Abonnements",
  miscLandPricingMonthlyNote:
    "Inklusive eines dauerhaft KOSTENLOSEN Tarifs. Jederzeit upgraden.",
  miscLandPeriodForever: "für immer",
  miscLandPeriodMonth: "Monat",
  miscLandPeriodOneTrip: "eine Reise",
  miscLandSingleAccess: "{days} Tage Zugang",
  miscLandPlanEmergencyData: "Notfall-Gesundheitsdaten",
  miscLandPlanQrAccess: "Zugriff per QR-Code",
  miscLandPlanBasicTravel: "Grundlegende Reiseplanung",
  miscLandPlanFiveReminders: "5 Medikamentenerinnerungen/Monat",
  miscLandPlanGdprStorage: "DSGVO-konforme Speicherung",
  miscLandPlanEverythingFree: "Alles aus FREE",
  miscLandPlanUnlimitedReminders: "Unbegrenzte Erinnerungen",
  miscLandPlanTravelReqs: "Gesundheitsanforderungen für Reisen",
  miscLandPlanVaccTracking: "Impfverwaltung",
  miscLandPlanStorage1: "Dokumentenspeicher (1 GB)",
  miscLandPlanPriority: "Bevorzugter Support",
  miscLandPlanOffline: "Offline-Zugriff",
  miscLandPlanEverythingPlus: "Alles aus PLUS",
  miscLandPlanCompanions: "Mitreisende auf einer Reise",
  miscLandPlanNearby:
    "Krankenhäuser, Apotheken und Kliniken in der Nähe Ihres Reiseziels",
  miscLandPlanDoctorSummary:
    "Gesundheitszusammenfassung der Reise an Ihre eigene Ärztin oder Ihren eigenen Arzt",
  miscLandPlanAdviceWatch:
    "Beobachtung sich ändernder Gesundheitshinweise für Ihre Reiseziele",
  miscLandPlanStorage10: "Dokumentenspeicher (10 GB)",
  miscLandPlanAllPlus: "Alle PLUS-Funktionen",
  miscLandPlanAllPremium: "Alle PREMIUM-Funktionen",
  miscLandChoosePlus: "PLUS wählen",
  miscLandChoosePremium: "PREMIUM wählen",
  miscLandBuySingleTrip: "Einzelreise kaufen",
  miscLandBadgeMostPopular: "Am beliebtesten",
  miscLandBadgeBestOneTrip: "Ideal für eine Reise",
  miscLandSingleTitle: "Tarife für eine Einzelreise",
  miscLandSingleNote:
    "Einmalzahlung. Voller Zugang für 90 Tage – kein Abonnement nötig.",

  miscLandSignIn: "Anmelden",
  miscLandCtaNote:
    "Keine Kreditkarte erforderlich • Dauerhaft KOSTENLOSER Tarif • Jederzeit upgraden",

  miscLandLogoAlt: "HealthPass-Logo",
  miscLandFooterTagline:
    "Ihre Gesundheitsdaten, immer dabei. Weltweit sicher reisen.",
  miscLandFooterProduct: "Produkt",
  miscLandFooterFeatures: "Funktionen",
  miscLandFooterPricing: "Preise",
  miscLandFooterSecurity: "Sicherheit",
  miscLandFooterFaq: "FAQ",
  miscLandFooterRecordsApp: "App für Reise-Gesundheitsdaten",
  miscLandFooterCompany: "Unternehmen",
  miscLandFooterBlog: "Blog",
  miscLandFooterContact: "Kontakt",
  miscLandFooterLegal: "Rechtliches",
  miscLandFooterPrivacy: "Datenschutzerklärung",
  miscLandFooterTerms: "Nutzungsbedingungen",
  miscLandFooterImprint: "Impressum",
  miscLandFooterCopyright:
    "© {year} HealthPass. Alle Rechte vorbehalten. Gemacht für Reisende.",
};

const fr: AppPagesLandingStrings = {
  miscLandSeoTitle:
    "Passeport santé de voyage – Votre dossier médical et carte d'urgence à l'étranger",
  miscLandSeoDescription:
    "HealthPass est un passeport santé de voyage : vos maladies, médicaments, allergies, vaccins et contacts d'urgence au même endroit, avec une carte d'urgence qu'un médecin à l'étranger lit en cinq langues. Gratuit pour commencer, sans app store.",

  miscLandHeroLanguagesBadge: "Disponible en 5 langues",
  miscLandHeroTravelerHeadline:
    "Votre passeport santé de voyage : dossier médical et carte d'urgence pour chaque voyage",
  miscLandHeroTravelerSubheadline:
    "HealthPass réunit vos maladies, médicaments, allergies, vaccins et contacts d'urgence au même endroit et place l'essentiel sur une carte d'urgence. Un médecin à l'étranger scanne son code QR et la lit en anglais, allemand, français, espagnol ou portugais, sans application ni connexion. La carte est gratuite et HealthPass fonctionne dans le navigateur : rien à télécharger depuis un app store.",
  miscLandFaqTitle: "Les questions des voyageurs",
  miscLandFaqQ1: "Qu'est-ce qu'un passeport santé de voyage ?",
  miscLandFaqA1:
    "Il garde prêtes au même endroit les informations médicales utiles en voyage : maladies, médicaments, allergies, vaccins et contacts d'urgence, ainsi que les documents de santé exigés par votre destination. HealthPass en est un, et sa carte d'urgence se lit à l'étranger sans application.",
  miscLandFaqQ2:
    "Un médecin à l'étranger peut-il lire mes informations médicales ?",
  miscLandFaqA2:
    "Oui. Votre carte d'urgence porte un code QR. En le scannant, on ouvre la carte dans un navigateur, en anglais, allemand, français, espagnol ou portugais, sans application ni connexion. Vous décidez de ce que la carte affiche.",
  miscLandFaqQ3: "Dois-je télécharger une application ?",
  miscLandFaqA3:
    "Non. HealthPass fonctionne dans le navigateur sur tout téléphone ou ordinateur, et vous pouvez l'ajouter à votre écran d'accueil comme une application. Il n'est ni sur l'App Store ni sur Google Play.",
  miscLandFaqQ4: "Combien coûte HealthPass ?",
  miscLandFaqA4:
    "La carte d'urgence et la planification de voyage de base sont gratuites à vie. PLUS coûte {plus} par mois et PREMIUM {premium} par mois ; pour un seul voyage, PLUS coûte {plusTrip} et PREMIUM {premiumTrip} pour 90 jours.",
  miscLandHeroNomadHeadline:
    "Un soutien santé pour les voyages flexibles et fréquents",
  miscLandHeroNomadSubheadline:
    "Gardez à portée de main vos informations de santé importantes, le détail de vos médicaments et l'accès d'urgence en passant d'une destination et d'un fuseau horaire à l'autre.",
  miscLandHeroCorporateHeadline:
    "Un soutien santé numérique pour les voyages d'affaires et professionnels",
  miscLandHeroCorporateSubheadline:
    "Préparez vos informations de santé importantes en un seul endroit pour vos voyages, l'enregistrement et les situations urgentes en déplacement.",
  miscLandHeroFamilyHeadline:
    "Un soutien pour les familles, les accompagnants et les voyages en groupe",
  miscLandHeroFamilySubheadline:
    "Gardez prêtes les informations médicales essentielles pour les enfants, les proches âgés et l'organisation commune du voyage.",
  miscLandHeroChronicHeadline:
    "Un soutien pour les voyageurs ayant des problèmes de santé",
  miscLandHeroChronicSubheadline:
    "Organisez vos informations médicales importantes, vos médicaments et l'accès d'urgence avant votre départ pour voyager plus sereinement.",
  miscLandCtaCreateFreeAccount: "Créer votre compte gratuit",
  miscLandCtaRequestDemo: "Demander une démo",
  miscLandFreeLifetimePlan: "Offre GRATUITE à vie",

  miscLandAudienceHealthMinded: "Voyageurs soucieux de leur santé",
  miscLandAudienceHealthIssues: "Voyageurs ayant des problèmes de santé",
  miscLandAudienceOlder: "Voyageurs âgés",
  miscLandAudienceDisabled: "Voyageurs en situation de handicap",
  miscLandAudienceActivities: "Voyageurs prévoyant des activités particulières",
  miscLandAudiencePlaneCruise: "Voyageurs en avion ou en croisière",

  miscLandMockEmergencyData: "Données d'urgence",
  miscLandMockAlwaysAccessible: "Toujours accessibles",
  miscLandMockScan: "Scanner",
  miscLandMockBloodType: "Groupe sanguin : A+",
  miscLandMockVerified: "Vérifié",
  miscLandMockAllergies: "Allergies : pénicilline",
  miscLandMockSevere: "Sévère",
  miscLandMockUpcomingTrip: "Prochain voyage",
  miscLandMockTripDestination: "Bangkok, Thaïlande",
  miscLandMockMedReminder: "Rappel de médicament",
  miscLandMockDailyAt: "Tous les jours à {time}",
  miscLandMockEncrypted: "Chiffré",

  miscLandTrustPrivacy: "Axé sur la confidentialité",
  miscLandTrustSecureAccess: "Accès numérique sécurisé",
  miscLandTrustManySituations: "Utile dans de nombreuses situations de voyage",
  miscLandTrustInternational: "Conçu pour les voyages internationaux",

  miscLandCardBadge: "Carte HEALTHPASS",
  miscLandCardTitle: "Une carte. Quatre actions clés pour voyager.",
  miscLandCardIntro:
    "La carte HEALTHPASS garde vos informations de santé importantes prêtes pour l'enregistrement, la communication en cas d'urgence, l'accès médical et votre portefeuille numérique.",
  miscLandCardPoint1:
    "Identité numérique et accès santé prêts pour le voyage, en un seul endroit",
  miscLandCardPoint2:
    "Partage rapide des informations importantes lorsque de l'aide est nécessaire",
  miscLandCardPoint3:
    "Conçue pour les voyages internationaux, les croisières et les voyages en avion",
  miscLandCardActionCheckIn: "Enregistrement",
  miscLandCardActionEmergency: "Urgence",
  miscLandCardActionMedAccess: "Accès médical",
  miscLandCardActionWallet: "Wallet",

  miscLandChronicTitle: "Vous vivez avec une maladie chronique ?",
  miscLandChronicIntro:
    "Ne laissez pas vos problèmes de santé vous empêcher de découvrir le monde. HEALTHPASS prend en charge un large éventail de maladies chroniques et de besoins de santé liés au voyage.",
  miscLandChronicDiabetesTitle: "Diabète (type 1 et 2)",
  miscLandChronicDiabetes1:
    "L'insuline et les autres médicaments dans une seule liste",
  miscLandChronicDiabetes2:
    "Glycémie et résultats d'analyses que vous saisissez vous-même",
  miscLandChronicDiabetes3: "Rappels de médicaments",
  miscLandChronicDiabetes4: "Diabète et allergies sur la carte d'urgence",
  miscLandChronicIbdTitle: "MICI (Crohn et colite)",
  miscLandChronicIbd1:
    "Biothérapies et ordonnances dans votre liste de médicaments",
  miscLandChronicIbd2:
    "Courriers médicaux enregistrés comme documents à présenter",
  miscLandChronicIbd3: "Pharmacies et cliniques près de votre destination",
  miscLandChronicIbd4: "Diagnostic et traitement sur la carte d'urgence",
  miscLandChronicNeuroTitle: "Neurologique (SEP, épilepsie)",
  miscLandChronicNeuro1: "Carte d'urgence lisible en cinq langues",
  miscLandChronicNeuro2: "Antiépileptiques et doses visibles par les secours",
  miscLandChronicNeuro3: "Contacts d'urgence que les secours peuvent appeler",
  miscLandChronicHospitalsNearby:
    "Hôpitaux et cliniques près de votre destination",
  miscLandChronicCardiacTitle: "Maladies cardiaques",
  miscLandChronicCardiac1:
    "Carte de stimulateur cardiaque ou de DAI enregistrée comme document",
  miscLandChronicCardiac2:
    "Anticoagulants dans votre liste de médicaments et vos rappels",
  miscLandChronicCardiac3: "Maladie, allergies et contacts sur la carte QR",
  miscLandChronicAutoimmuneTitle: "Auto-immunes (polyarthrite, lupus)",
  miscLandChronicAutoimmune1:
    "Immunosuppresseurs dans votre liste de médicaments",
  miscLandChronicAutoimmune2:
    "Carnet de vaccination à portée de main quand un médecin le demande",
  miscLandChronicAutoimmune3:
    "Exigences sanitaires de la destination avant le départ",
  miscLandChronicAutoimmune4:
    "Cliniques et pharmacies près de votre destination",
  miscLandChronicKidneyTitle: "Maladie rénale",
  miscLandChronicKidney1:
    "Historique de dialyse et résultats d'analyses dans votre dossier",
  miscLandChronicKidney2: "Médicaments et doses dans une seule liste",
  miscLandChronicKidney3: "Maladie rénale sur la carte d'urgence",
  miscLandConfidenceTitle: "Voyagez en toute confiance",
  miscLandConfidenceText:
    "Vous pouvez voyager sans crainte, même si vous vivez avec des problèmes de santé. Découvrez comment avec HEALTHPASS. Vous n'avez plus besoin de rester chez vous.",
  miscLandConfidencePoint1:
    "Informations de santé importantes prêtes quand il le faut",
  miscLandConfidencePoint2:
    "Meilleure préparation à différents types de voyage",
  miscLandConfidencePoint3:
    "Communication plus rapide et plus claire lors d'un incident médical",
  miscLandConfidencePoint4: "Une expérience de voyage plus sûre et plus fluide",
  miscLandConfidenceQuote:
    "Voyager peut redevenir possible lorsque les bonnes informations sont disponibles au bon moment.",
  miscLandStatsMillions: "Des millions de personnes dans le monde",
  miscLandStatsLiveWith: "vivent avec une maladie chronique.",
  miscLandStatsAvoid: "renoncent à voyager par crainte.",
  miscLandStatsReconsider:
    "Avec HEALTHPASS, le voyage peut être envisagé à nouveau.",
  miscLandStartJourney: "Commencez votre voyage dès aujourd'hui",

  miscLandWhyTitle: "Pourquoi HEALTHPASS compte",
  miscLandWhyIntro:
    "HEALTHPASS vous aide à comprendre les risques sanitaires auxquels vous pouvez être exposé pendant votre voyage et vous accompagne dans votre préparation.",
  miscLandWhyPrepTitle: "Une meilleure préparation avant et pendant le voyage",
  miscLandWhyPrepText1:
    "HEALTHPASS vous aide à comprendre les risques sanitaires liés à votre destination, à votre type de voyage ou à vos activités prévues. Il propose des services qui peuvent contribuer à réduire ces risques.",
  miscLandWhyPrepText2:
    "En cas d'incident médical, les bonnes informations peuvent être transmises plus rapidement et plus clairement, ce qui favorise une décision de traitement meilleure et plus adaptée.",
  miscLandWhySeamlessTitle: "Une expérience de voyage numérique plus fluide",
  miscLandWhySeamlessText1:
    "Vos échanges avec les voyagistes ou d'autres partenaires de voyage qui demandent des informations de santé deviennent une expérience numérique plus fluide.",
  miscLandWhySeamlessText2:
    "Plus de formulaires papier, plus besoin de ressaisir les mêmes informations à chaque voyage, et moins de complications à l'enregistrement ou aux frontières.",
  miscLandWhyBanner:
    "Il contribue à rendre les voyages plus sûrs et l'expérience globale plus fluide.",

  miscLandSupportTitle:
    "Voyager en avion ou en croisière en étant mieux préparé",
  miscLandSupportIntro:
    "Gardez vos informations de santé essentielles prêtes pour l'enregistrement, le passage des frontières et les situations médicales urgentes en voyage.",
  miscLandSupportRiskTitle: "Connaissance des risques",
  miscLandSupportRiskText:
    "Comprendre les risques sanitaires pertinents avant le départ.",
  miscLandSupportFasterTitle: "Aide plus rapide",
  miscLandSupportFasterText:
    "Partager plus vite les informations médicales essentielles quand cela compte.",
  miscLandSupportRepetitionTitle: "Moins de répétitions",
  miscLandSupportRepetitionText:
    "Ne plus ressaisir les mêmes informations de santé à chaque voyage.",
  miscLandSupportConfidenceTitle: "Plus de confiance",
  miscLandSupportConfidenceText:
    "Voyager plus sereinement, du départ à l'arrivée.",
  miscLandDemoTitle: "Réservez une démo en direct",
  miscLandDemoText:
    "Découvrez comment HEALTHPASS peut contribuer à des voyages plus sûrs et plus fluides.",
  miscLandDemoCta: "Réserver une démo",

  miscLandIssuesTitle: "Pour les voyageurs ayant des problèmes de santé",
  miscLandIssuesIntro:
    "HEALTHPASS peut vous aider à préparer les informations importantes avant votre voyage pour voyager avec plus de confiance et de sérénité.",
  miscLandIssuesFearTitle: "Voyager sans crainte",
  miscLandIssuesFearSubtitle:
    "Une meilleure préparation aux situations médicales et liées au voyage",
  miscLandIssuesText1:
    "HEALTHPASS aide à organiser les informations clés sur les maladies chroniques, les handicaps, les médicaments, les allergies et d'autres aspects de santé qui peuvent compter pendant un voyage.",
  miscLandIssuesText2:
    "Cela peut favoriser une communication plus rapide et une expérience numérique plus fluide lorsque des informations de santé sont demandées.",
  miscLandIssuesHelpTitle: "Ce en quoi HEALTHPASS peut vous aider :",
  miscLandIssuesHelp1: "Informations importantes prêtes quand il le faut",
  miscLandIssuesHelp2:
    "Meilleure préparation aux risques et aux activités du voyage",
  miscLandIssuesHelp3:
    "Partage plus fluide lors de l'enregistrement ou des demandes d'aide",
  miscLandIssuesHelp4: "Plus de confiance avant et pendant le voyage",
  miscLandIssuesCta:
    "Commencez gratuitement, passez à l'offre supérieure pour voyager",
  miscLandIssuesCtaNote:
    "L'offre gratuite conserve votre carte d'urgence. PLUS ajoute les exigences sanitaires de la destination et des rappels de médicaments illimités pour {price}/mois.",

  miscLandFeatureEmergencyTitle: "Données de santé d'urgence",
  miscLandFeatureEmergencyDesc:
    "Vos informations de santé essentielles disponibles 24 h/24 et 7 j/7 via un QR code sécurisé",
  miscLandFeatureGdprTitle: "Conçu pour le RGPD",
  miscLandFeatureGdprDesc:
    "Chiffré en transit et au repos, chaque accès à un dossier est journalisé",
  miscLandFeatureTravelTitle: "Planification de voyage",
  miscLandFeatureTravelDesc:
    "Exigences sanitaires propres à chaque destination et rappels de vaccination",
  miscLandFeatureMedsTitle: "Rappels de médicaments",
  miscLandFeatureMedsDesc:
    "Ne manquez plus aucune prise grâce à un suivi intelligent des médicaments",
  miscLandFeatureLanguagesTitle: "Cinq langues",
  miscLandFeatureLanguagesDesc:
    "L'application et la vue d'urgence sont disponibles en anglais, allemand, français, espagnol et portugais.",
  miscLandFeatureMobileTitle: "Prêt pour le mobile",
  miscLandFeatureMobileDesc:
    "Accédez à vos données de santé à tout moment, partout, sur n'importe quel appareil",

  miscLandStep1Title: "Créer un compte",
  miscLandStep1Desc:
    "Inscrivez-vous en 2 minutes avec votre seule adresse e-mail",
  miscLandStep2Title: "Ajouter vos données de santé",
  miscLandStep2Desc: "Saisissez vos allergies, médicaments et maladies",
  miscLandStep3Title: "Voyager en sécurité",
  miscLandStep3Desc: "Obtenez votre QR code et voyagez l'esprit tranquille",

  miscLandHubTitle: "Voyager avec une maladie chronique ?",
  miscLandHubIntro:
    "HEALTHPASS prend en charge un large éventail de maladies chroniques avec des informations prêtes pour le voyage, une assistance multilingue et des outils d'accès d'urgence. Vous n'avez pas à rester chez vous.",
  miscLandHubDiabetes1: "Liste d'insuline et de médicaments",
  miscLandHubDiabetes2: "Carnet de glycémie",
  miscLandHubDiabetes3: "Rappel pour chaque prise",
  miscLandHubHeartTitle: "Maladie cardiaque",
  miscLandHubHeart1: "Documents du stimulateur cardiaque",
  miscLandHubHeart2: "Rappels d'anticoagulants",
  miscLandHubConditionOnQr: "Maladie indiquée sur la carte QR",
  miscLandHubCopdTitle: "BPCO et asthme",
  miscLandHubCopd1: "Formulaires d'oxygène en documents",
  miscLandHubCopd2: "Rappels d'inhalateur",
  miscLandHubCopd3: "Pharmacies sur votre lieu de destination",
  miscLandHubCrohnsTitle: "Crohn et colite",
  miscLandHubCrohns1: "Biothérapies dans votre liste de médicaments",
  miscLandHubCrohns2: "Courriers enregistrés comme documents",
  miscLandHubClinics: "Cliniques sur votre lieu de destination",
  miscLandHubEpilepsyTitle: "Épilepsie et crises",
  miscLandHubEpilepsy1: "Carte d'urgence en 5 langues",
  miscLandHubEpilepsy2: "Antiépileptiques et doses",
  miscLandHubHospitals: "Hôpitaux sur votre lieu de destination",
  miscLandHubKidney1: "Historique de dialyse et analyses",
  miscLandHubKidney2: "Médicaments et doses",
  miscLandHubRaTitle: "Polyarthrite rhumatoïde",
  miscLandHubRa1: "Liste des immunosuppresseurs",
  miscLandHubRa2: "Carnet de vaccination",
  miscLandHubMsTitle: "Sclérose en plaques",
  miscLandHubMs1: "Médicaments et perfusions",
  miscLandHubReadGuide: "Lire le guide de voyage",
  miscLandHubMoreGuides: "Autres guides santé voyage (en anglais)",
  miscLandHubCta: "Commencer avec l'offre GRATUITE à vie",
  miscLandHubNote:
    "Aucune carte bancaire requise · Fonctionne avec votre suivi médical actuel",

  miscLandCasesTitle: "Exemples de situations de voyage",
  miscLandCasesIntro:
    "Des exemples illustrant comment HEALTHPASS peut contribuer à des voyages plus sûrs et plus fluides.",
  miscLandChallenge: "Défi :",
  miscLandSolution: "Solution :",
  miscLandCaseAllReturned: "Les 22 sont rentrés sains et saufs",
  miscLandCaseGroupTitle: "22 voyageurs, 22 QR codes",
  miscLandCaseGroupSubtitle: "Circuit d'un groupe de seniors au Portugal",
  miscLandCaseGroupChallenge:
    "Inge (72 ans, stimulateur cardiaque), Werner (75 ans, opération de la hanche), Helga (68 ans, dialyse) – 22 seniors, 110 médicaments, 10 jours au Portugal.",
  miscLandCaseGroupSolution:
    "Chaque voyageur avait son propre QR code : un ambulancier pouvait lire les médicaments et les maladies sans attendre de joindre un proche.",
  miscLandCaseGroupResult2: "Maladies lisibles sans interprète",
  miscLandCaseGroupResult3:
    "Aucun proche nécessaire pour confirmer un médicament",
  miscLandCaseCruiseTitle: "Une croisière en toute confiance",
  miscLandCaseCruiseSubtitle: "Le voyage de Maria en Méditerranée",
  miscLandCaseCruiseChallenge:
    "Maria (62 ans) est diabétique, allergique à la pénicilline et porte un stimulateur cardiaque. Croisière de 14 jours en Grèce, en Italie et en Croatie.",
  miscLandCaseCruiseSolution:
    "Un bracelet QR code avec ses données de santé en cinq langues, des rappels de médicaments et un accès immédiat pour les médecins de bord.",
  miscLandCaseCruiseResult1: "Aucune prise de médicament oubliée",
  miscLandCaseCruiseResult2:
    "Pas besoin de chercher une liste de médicaments dans un port étranger",
  miscLandCaseCruiseResult3:
    "Le médecin de bord voit immédiatement son stimulateur et son allergie",
  miscLandCaseFamilyTitle: "Voyager en famille en toute sécurité",
  miscLandCaseFamilySubtitle: "La famille Müller en Thaïlande",
  miscLandCaseFamilyChallenge:
    "Famille de 4 personnes, Emma (8 ans) souffre d'une allergie aux arachides potentiellement mortelle. 3 semaines en Thaïlande et au Cambodge.",
  miscLandCaseFamilySolution:
    "Un QR code pour Emma indiquant l'allergie et son stylo d'adrénaline, avec ses parents comme contacts d'urgence, prévenus par e-mail dès qu'il est lu.",
  miscLandCaseFamilyResult1: "Informations importantes disponibles rapidement",
  miscLandCaseFamilyResult2:
    "L'allergie est indiquée avant même qu'on la demande",
  miscLandCaseFamilyResult3:
    "Les parents reçoivent un e-mail quand le code est lu",
  miscLandCaseSoloTitle: "Voyager seul, sans être seul",
  miscLandCaseSoloSubtitle: "Le tour du monde de 6 mois de Max",
  miscLandCaseSoloChallenge:
    "Max (26 ans), diabétique de type 1, voyage seul dans 12 pays. Gestion de l'insuline, activités extrêmes, régions isolées.",
  miscLandCaseSoloSolution:
    "Des données de santé lisibles hors ligne, un QR code imprimé sur le sac à dos, son insuline dans la liste de médicaments et ses proches prévenus quand le code est lu.",
  miscLandCaseSoloResult1: "Informations d'urgence disponibles hors ligne",
  miscLandCaseSoloResult2: "Insuline et doses lisibles par un inconnu",
  miscLandCaseSoloResult3: "Le diabète est connu avant le début du traitement",
  miscLandCaseSeniorsTitle: "Les voyages en groupe simplifiés",
  miscLandCaseSeniorsSubtitle: "Un groupe de seniors au Portugal",
  miscLandCaseSeniorsChallenge:
    "22 seniors (60 à 78 ans) avec 110 médicaments au total. Stimulateurs cardiaques, dialyse, antécédents d'AVC, perte auditive.",
  miscLandCaseSeniorsSolution:
    "Un QR code par voyageur, avec médicaments, maladies et contacts d'urgence, ainsi que les hôpitaux et cliniques près de chaque étape.",
  miscLandCaseSeniorsResult2: "110 médicaments documentés, pas mémorisés",
  miscLandCaseSeniorsResult3:
    "Les contacts de chaque voyageur joignables sur-le-champ",
  miscLandCasesMore: "Voir d'autres exemples de voyage",

  miscLandPricingMonthlyTitle: "Abonnements mensuels",
  miscLandPricingMonthlyNote:
    "Comprend une offre GRATUITE à vie. Évoluez à tout moment.",
  miscLandPeriodForever: "pour toujours",
  miscLandPeriodMonth: "mois",
  miscLandPeriodOneTrip: "un voyage",
  miscLandSingleAccess: "{days} jours d'accès",
  miscLandPlanEmergencyData: "Données de santé d'urgence",
  miscLandPlanQrAccess: "Accès par QR code",
  miscLandPlanBasicTravel: "Planification de voyage de base",
  miscLandPlanFiveReminders: "5 rappels de médicaments/mois",
  miscLandPlanGdprStorage: "Stockage conforme au RGPD",
  miscLandPlanEverythingFree: "Tout ce qu'inclut FREE",
  miscLandPlanUnlimitedReminders: "Rappels illimités",
  miscLandPlanTravelReqs: "Exigences sanitaires de voyage",
  miscLandPlanVaccTracking: "Suivi des vaccinations",
  miscLandPlanStorage1: "Stockage de documents (1 Go)",
  miscLandPlanPriority: "Assistance prioritaire",
  miscLandPlanOffline: "Accès hors ligne",
  miscLandPlanEverythingPlus: "Tout ce qu'inclut PLUS",
  miscLandPlanCompanions: "Compagnons de voyage sur un trajet",
  miscLandPlanNearby:
    "Hôpitaux, pharmacies et cliniques près de votre destination",
  miscLandPlanDoctorSummary:
    "Résumé santé du voyage envoyé à votre propre médecin",
  miscLandPlanAdviceWatch:
    "Veille sur l'évolution des recommandations sanitaires pour vos destinations",
  miscLandPlanStorage10: "Stockage de documents (10 Go)",
  miscLandPlanAllPlus: "Toutes les fonctionnalités PLUS",
  miscLandPlanAllPremium: "Toutes les fonctionnalités PREMIUM",
  miscLandChoosePlus: "Choisir PLUS",
  miscLandChoosePremium: "Choisir PREMIUM",
  miscLandBuySingleTrip: "Acheter un voyage unique",
  miscLandBadgeMostPopular: "Le plus populaire",
  miscLandBadgeBestOneTrip: "Idéal pour un voyage",
  miscLandSingleTitle: "Offres pour un voyage unique",
  miscLandSingleNote:
    "Paiement unique. Accès complet pendant 90 jours – sans abonnement.",

  miscLandSignIn: "Se connecter",
  miscLandCtaNote:
    "Aucune carte bancaire requise • Offre GRATUITE à vie • Évoluez à tout moment",

  miscLandLogoAlt: "Logo HealthPass",
  miscLandFooterTagline:
    "Vos données de santé, toujours avec vous. Voyagez en sécurité partout dans le monde.",
  miscLandFooterProduct: "Produit",
  miscLandFooterFeatures: "Fonctionnalités",
  miscLandFooterPricing: "Tarifs",
  miscLandFooterSecurity: "Sécurité",
  miscLandFooterFaq: "FAQ",
  miscLandFooterRecordsApp: "Application de dossier médical de voyage",
  miscLandFooterCompany: "Entreprise",
  miscLandFooterBlog: "Blog",
  miscLandFooterContact: "Contact",
  miscLandFooterLegal: "Juridique",
  miscLandFooterPrivacy: "Politique de confidentialité",
  miscLandFooterTerms: "Conditions d'utilisation",
  miscLandFooterImprint: "Mentions légales",
  miscLandFooterCopyright:
    "© {year} HealthPass. Tous droits réservés. Conçu pour les voyageurs.",
};

const es: AppPagesLandingStrings = {
  miscLandSeoTitle:
    "Pasaporte de salud para viajes – Su historial médico y tarjeta de emergencia en el extranjero",
  miscLandSeoDescription:
    "HealthPass es un pasaporte de salud para viajes: sus enfermedades, medicamentos, alergias, vacunas y contactos de emergencia en un solo lugar, con una tarjeta de emergencia que un médico en el extranjero lee en cinco idiomas. Gratis para empezar, sin tienda de aplicaciones.",

  miscLandHeroLanguagesBadge: "Disponible en 5 idiomas",
  miscLandHeroTravelerHeadline:
    "Su pasaporte de salud para viajes: historial médico y tarjeta de emergencia para cada viaje",
  miscLandHeroTravelerSubheadline:
    "HealthPass guarda sus enfermedades, medicamentos, alergias, vacunas y contactos de emergencia en un solo lugar y reúne lo esencial en una tarjeta de emergencia. Un médico en el extranjero escanea su código QR y la lee en inglés, alemán, francés, español o portugués, sin aplicación y sin iniciar sesión. La tarjeta es gratuita y HealthPass funciona en el navegador, así que no hay nada que descargar de una tienda de aplicaciones.",
  miscLandFaqTitle: "Preguntas de los viajeros",
  miscLandFaqQ1: "¿Qué es un pasaporte de salud para viajes?",
  miscLandFaqA1:
    "Tiene a mano en un solo lugar la información médica que necesita en un viaje: enfermedades, medicamentos, alergias, vacunas y contactos de emergencia, junto con los documentos de salud que exige su destino. HealthPass es uno, y su tarjeta de emergencia se puede leer en el extranjero sin aplicación.",
  miscLandFaqQ2:
    "¿Puede un médico en el extranjero leer mi información médica?",
  miscLandFaqA2:
    "Sí. Su tarjeta de emergencia tiene un código QR. Al escanearlo se abre la tarjeta en un navegador, en inglés, alemán, francés, español o portugués, sin aplicación y sin iniciar sesión. Usted decide qué muestra la tarjeta.",
  miscLandFaqQ3: "¿Tengo que descargar una aplicación?",
  miscLandFaqA3:
    "No. HealthPass funciona en el navegador de cualquier teléfono u ordenador y puede añadirlo a la pantalla de inicio como una aplicación. No está en App Store ni en Google Play.",
  miscLandFaqQ4: "¿Cuánto cuesta HealthPass?",
  miscLandFaqA4:
    "La tarjeta de emergencia y la planificación básica de viajes son gratuitas para siempre. PLUS cuesta {plus} al mes y PREMIUM {premium} al mes; para un solo viaje, PLUS cuesta {plusTrip} y PREMIUM {premiumTrip} durante 90 días.",
  miscLandHeroNomadHeadline:
    "Apoyo sanitario para viajes flexibles y frecuentes",
  miscLandHeroNomadSubheadline:
    "Tenga a mano su información de salud importante, los datos de su medicación y el acceso de emergencia mientras se desplaza entre destinos y husos horarios.",
  miscLandHeroCorporateHeadline:
    "Apoyo sanitario digital para viajes de negocios y de trabajo",
  miscLandHeroCorporateSubheadline:
    "Prepare su información de salud importante en un solo lugar para sus viajes, el check-in y las situaciones urgentes durante el trayecto.",
  miscLandHeroFamilyHeadline:
    "Apoyo para familias, acompañantes y viajes en grupo",
  miscLandHeroFamilySubheadline:
    "Tenga listos los datos médicos más importantes de los niños, los familiares mayores y la planificación conjunta del viaje.",
  miscLandHeroChronicHeadline: "Apoyo para viajeros con problemas de salud",
  miscLandHeroChronicSubheadline:
    "Organice su información médica importante, sus medicamentos y el acceso de emergencia antes del viaje para viajar con más confianza.",
  miscLandCtaCreateFreeAccount: "Cree su cuenta gratuita",
  miscLandCtaRequestDemo: "Solicitar una demostración",
  miscLandFreeLifetimePlan: "Plan GRATUITO de por vida",

  miscLandAudienceHealthMinded: "Viajeros que cuidan su salud",
  miscLandAudienceHealthIssues: "Viajeros con problemas de salud",
  miscLandAudienceOlder: "Viajeros mayores",
  miscLandAudienceDisabled: "Viajeros con discapacidad",
  miscLandAudienceActivities: "Viajeros que planean actividades especiales",
  miscLandAudiencePlaneCruise: "Viajeros en avión o en crucero",

  miscLandMockEmergencyData: "Datos de emergencia",
  miscLandMockAlwaysAccessible: "Siempre accesibles",
  miscLandMockScan: "Escanear",
  miscLandMockBloodType: "Grupo sanguíneo: A+",
  miscLandMockVerified: "Verificado",
  miscLandMockAllergies: "Alergias: penicilina",
  miscLandMockSevere: "Grave",
  miscLandMockUpcomingTrip: "Próximo viaje",
  miscLandMockTripDestination: "Bangkok, Tailandia",
  miscLandMockMedReminder: "Recordatorio de medicación",
  miscLandMockDailyAt: "Diario a las {time}",
  miscLandMockEncrypted: "Cifrado",

  miscLandTrustPrivacy: "Centrado en la privacidad",
  miscLandTrustSecureAccess: "Acceso digital seguro",
  miscLandTrustManySituations: "Útil en muchas situaciones de viaje",
  miscLandTrustInternational: "Diseñado para viajes internacionales",

  miscLandCardBadge: "Tarjeta HEALTHPASS",
  miscLandCardTitle: "Una tarjeta. Cuatro acciones clave para viajar.",
  miscLandCardIntro:
    "La tarjeta HEALTHPASS tiene lista su información de salud importante para el check-in, la comunicación en emergencias, el acceso médico y su cartera digital.",
  miscLandCardPoint1:
    "Identidad digital y acceso sanitario listos para viajar, en un solo lugar",
  miscLandCardPoint2:
    "Comparta rápidamente información importante cuando necesite ayuda",
  miscLandCardPoint3:
    "Diseñada para viajes internacionales, cruceros y viajes en avión",
  miscLandCardActionCheckIn: "Check-in",
  miscLandCardActionEmergency: "Emergencia",
  miscLandCardActionMedAccess: "Acceso médico",
  miscLandCardActionWallet: "Wallet",

  miscLandChronicTitle: "¿Vive con una enfermedad crónica?",
  miscLandChronicIntro:
    "No deje que los problemas de salud le impidan descubrir el mundo. HEALTHPASS da soporte a una amplia variedad de enfermedades crónicas y necesidades de salud relacionadas con los viajes.",
  miscLandChronicDiabetesTitle: "Diabetes (tipo 1 y 2)",
  miscLandChronicDiabetes1:
    "La insulina y otros medicamentos en una sola lista",
  miscLandChronicDiabetes2:
    "Glucemia y valores de laboratorio que registra usted mismo",
  miscLandChronicDiabetes3: "Recordatorios de medicación",
  miscLandChronicDiabetes4: "Diabetes y alergias en la tarjeta de emergencia",
  miscLandChronicIbdTitle: "EII (Crohn y colitis)",
  miscLandChronicIbd1: "Biológicos y recetas en su lista de medicamentos",
  miscLandChronicIbd2:
    "Informes médicos guardados como documentos que puede mostrar",
  miscLandChronicIbd3: "Farmacias y clínicas cerca de su destino",
  miscLandChronicIbd4: "Diagnóstico y medicación en la tarjeta de emergencia",
  miscLandChronicNeuroTitle: "Neurológicas (EM, epilepsia)",
  miscLandChronicNeuro1: "Tarjeta de emergencia legible en cinco idiomas",
  miscLandChronicNeuro2:
    "Anticonvulsivos y dosis visibles para los servicios de emergencia",
  miscLandChronicNeuro3:
    "Contactos de emergencia a los que pueden llamar los equipos de auxilio",
  miscLandChronicHospitalsNearby: "Hospitales y clínicas cerca de su destino",
  miscLandChronicCardiacTitle: "Enfermedades cardíacas",
  miscLandChronicCardiac1:
    "Tarjeta de marcapasos o DAI guardada como documento",
  miscLandChronicCardiac2:
    "Anticoagulantes en su lista de medicamentos y recordatorios",
  miscLandChronicCardiac3: "Enfermedad, alergias y contactos en la tarjeta QR",
  miscLandChronicAutoimmuneTitle: "Autoinmunes (artritis reumatoide, lupus)",
  miscLandChronicAutoimmune1: "Inmunosupresores en su lista de medicamentos",
  miscLandChronicAutoimmune2:
    "Registro de vacunación a mano cuando un médico lo pida",
  miscLandChronicAutoimmune3:
    "Requisitos sanitarios del destino antes de salir",
  miscLandChronicAutoimmune4: "Clínicas y farmacias cerca de su destino",
  miscLandChronicKidneyTitle: "Enfermedad renal",
  miscLandChronicKidney1:
    "Historial de diálisis y valores de laboratorio en sus registros",
  miscLandChronicKidney2: "Medicamentos y dosis en una sola lista",
  miscLandChronicKidney3: "Enfermedad renal en la tarjeta de emergencia",
  miscLandConfidenceTitle: "Viaje con confianza",
  miscLandConfidenceText:
    "Puede viajar sin miedo aunque conviva con problemas de salud. Descubra cómo con HEALTHPASS. Ya no tiene que quedarse en casa.",
  miscLandConfidencePoint1:
    "Información de salud importante lista cuando se necesita",
  miscLandConfidencePoint2: "Mejor preparación para distintos tipos de viaje",
  miscLandConfidencePoint3:
    "Comunicación más rápida y clara ante un incidente médico",
  miscLandConfidencePoint4: "Una experiencia de viaje más segura y fluida",
  miscLandConfidenceQuote:
    "Viajar puede volver a ser posible cuando la información adecuada está disponible en el momento adecuado.",
  miscLandStatsMillions: "Millones de personas en todo el mundo",
  miscLandStatsLiveWith: "viven con enfermedades crónicas.",
  miscLandStatsAvoid: "evitan viajar por miedo.",
  miscLandStatsReconsider: "Con HEALTHPASS, viajar vuelve a ser una opción.",
  miscLandStartJourney: "Empiece su viaje hoy",

  miscLandWhyTitle: "Por qué importa HEALTHPASS",
  miscLandWhyIntro:
    "HEALTHPASS le ayuda a entender los riesgos de salud a los que puede estar expuesto durante el viaje y le apoya en su preparación.",
  miscLandWhyPrepTitle: "Mejor preparación antes y durante el viaje",
  miscLandWhyPrepText1:
    "HEALTHPASS le ayuda a entender los riesgos de salud que pueden estar relacionados con su destino, su tipo de viaje o sus actividades previstas. Ofrece servicios que pueden ayudar a reducir esos riesgos.",
  miscLandWhyPrepText2:
    "En caso de incidente médico, la información adecuada puede estar disponible más rápido y con mayor claridad, lo que favorece una decisión de tratamiento mejor y más apropiada.",
  miscLandWhySeamlessTitle: "Una experiencia de viaje digital más fluida",
  miscLandWhySeamlessText1:
    "Su relación con touroperadores u otros socios de viaje que solicitan información de salud se convierte en una experiencia digital más fluida.",
  miscLandWhySeamlessText2:
    "Sin formularios en papel, sin volver a introducir los mismos datos en cada viaje y con menos fricción en el check-in o al cruzar fronteras.",
  miscLandWhyBanner:
    "Ayuda a que los viajes sean más seguros y la experiencia en conjunto más fluida.",

  miscLandSupportTitle: "Viaje en avión o en crucero mejor preparado",
  miscLandSupportIntro:
    "Tenga lista su información de salud clave para el check-in, los pasos fronterizos y las situaciones médicas urgentes durante el viaje.",
  miscLandSupportRiskTitle: "Conciencia del riesgo",
  miscLandSupportRiskText:
    "Comprenda los riesgos de salud relevantes antes de salir.",
  miscLandSupportFasterTitle: "Ayuda más rápida",
  miscLandSupportFasterText:
    "Comparta la información médica clave más rápido cuando importa.",
  miscLandSupportRepetitionTitle: "Menos repeticiones",
  miscLandSupportRepetitionText:
    "Evite volver a introducir los mismos datos de salud en cada viaje.",
  miscLandSupportConfidenceTitle: "Más confianza",
  miscLandSupportConfidenceText:
    "Viaje con más tranquilidad desde la salida hasta la llegada.",
  miscLandDemoTitle: "Reserve una demostración en directo",
  miscLandDemoText:
    "Descubra cómo HEALTHPASS puede contribuir a viajes más seguros y fluidos.",
  miscLandDemoCta: "Reservar una demostración",

  miscLandIssuesTitle: "Para viajeros con problemas de salud",
  miscLandIssuesIntro:
    "HEALTHPASS puede ayudarle a preparar la información importante antes del viaje para viajar con más confianza y tranquilidad.",
  miscLandIssuesFearTitle: "Viajar sin miedo",
  miscLandIssuesFearSubtitle:
    "Mejor preparación para situaciones médicas y de viaje",
  miscLandIssuesText1:
    "HEALTHPASS ayuda a organizar la información clave sobre enfermedades crónicas, discapacidades, medicamentos, alergias y otros aspectos de salud que pueden ser importantes durante un viaje.",
  miscLandIssuesText2:
    "Esto puede facilitar una comunicación más rápida y una experiencia digital más fluida cuando se solicita información de salud.",
  miscLandIssuesHelpTitle: "En qué puede ayudarle HEALTHPASS:",
  miscLandIssuesHelp1: "Información importante lista cuando se necesita",
  miscLandIssuesHelp2:
    "Mejor preparación ante los riesgos y las actividades del viaje",
  miscLandIssuesHelp3: "Intercambio más fluido en el check-in o al pedir ayuda",
  miscLandIssuesHelp4: "Más confianza antes y durante el viaje",
  miscLandIssuesCta: "Empiece gratis y mejore su plan cuando viaje",
  miscLandIssuesCtaNote:
    "El plan gratuito mantiene su tarjeta de emergencia. PLUS añade los requisitos sanitarios del destino y recordatorios de medicación ilimitados por {price}/mes.",

  miscLandFeatureEmergencyTitle: "Datos de salud de emergencia",
  miscLandFeatureEmergencyDesc:
    "Su información de salud crítica disponible 24/7 mediante un código QR seguro",
  miscLandFeatureGdprTitle: "Diseñado para el RGPD",
  miscLandFeatureGdprDesc:
    "Cifrado en tránsito y en reposo, cada acceso a un registro queda registrado",
  miscLandFeatureTravelTitle: "Planificación de viajes",
  miscLandFeatureTravelDesc:
    "Requisitos sanitarios específicos de cada destino y recordatorios de vacunación",
  miscLandFeatureMedsTitle: "Recordatorios de medicación",
  miscLandFeatureMedsDesc:
    "No vuelva a olvidar una dosis gracias al seguimiento inteligente de la medicación",
  miscLandFeatureLanguagesTitle: "Cinco idiomas",
  miscLandFeatureLanguagesDesc:
    "La aplicación y la vista de emergencia están disponibles en inglés, alemán, francés, español y portugués.",
  miscLandFeatureMobileTitle: "Listo para el móvil",
  miscLandFeatureMobileDesc:
    "Acceda a sus datos de salud en cualquier momento y lugar, desde cualquier dispositivo",

  miscLandStep1Title: "Crear una cuenta",
  miscLandStep1Desc: "Regístrese en 2 minutos solo con su correo electrónico",
  miscLandStep2Title: "Añadir datos de salud",
  miscLandStep2Desc: "Introduzca sus alergias, medicamentos y enfermedades",
  miscLandStep3Title: "Viajar seguro",
  miscLandStep3Desc: "Obtenga su código QR y viaje con tranquilidad",

  miscLandHubTitle: "¿Viaja con una enfermedad crónica?",
  miscLandHubIntro:
    "HEALTHPASS da soporte a una amplia variedad de enfermedades crónicas con información lista para viajar, asistencia multilingüe y herramientas de acceso de emergencia. No tiene que quedarse en casa.",
  miscLandHubDiabetes1: "Lista de insulina y medicamentos",
  miscLandHubDiabetes2: "Registro de glucemia",
  miscLandHubDiabetes3: "Recordatorio para cada dosis",
  miscLandHubHeartTitle: "Enfermedad cardíaca",
  miscLandHubHeart1: "Documentos del marcapasos",
  miscLandHubHeart2: "Recordatorios de anticoagulantes",
  miscLandHubConditionOnQr: "Enfermedad indicada en la tarjeta QR",
  miscLandHubCopdTitle: "EPOC y asma",
  miscLandHubCopd1: "Formularios de oxígeno como documentos",
  miscLandHubCopd2: "Recordatorios del inhalador",
  miscLandHubCopd3: "Farmacias en su destino",
  miscLandHubCrohnsTitle: "Crohn y colitis",
  miscLandHubCrohns1: "Biológicos en su lista de medicamentos",
  miscLandHubCrohns2: "Informes guardados como documentos",
  miscLandHubClinics: "Clínicas en su destino",
  miscLandHubEpilepsyTitle: "Epilepsia y convulsiones",
  miscLandHubEpilepsy1: "Tarjeta de emergencia en 5 idiomas",
  miscLandHubEpilepsy2: "Anticonvulsivos y dosis",
  miscLandHubHospitals: "Hospitales en su destino",
  miscLandHubKidney1: "Historial de diálisis y análisis",
  miscLandHubKidney2: "Medicamentos y dosis",
  miscLandHubRaTitle: "Artritis reumatoide",
  miscLandHubRa1: "Lista de inmunosupresores",
  miscLandHubRa2: "Registro de vacunación",
  miscLandHubMsTitle: "Esclerosis múltiple",
  miscLandHubMs1: "Medicamentos e infusiones",
  miscLandHubReadGuide: "Leer la guía de viaje",
  miscLandHubMoreGuides: "Más guías de salud para viajar (en inglés)",
  miscLandHubCta: "Empiece con el plan GRATUITO de por vida",
  miscLandHubNote:
    "No se requiere tarjeta de crédito · Compatible con su atención médica actual",

  miscLandCasesTitle: "Ejemplos de situaciones de viaje",
  miscLandCasesIntro:
    "Ejemplos ilustrativos de cómo HEALTHPASS puede contribuir a viajes más seguros y fluidos.",
  miscLandChallenge: "Reto:",
  miscLandSolution: "Solución:",
  miscLandCaseAllReturned: "Los 22 regresaron sanos y salvos",
  miscLandCaseGroupTitle: "22 viajeros, 22 códigos QR",
  miscLandCaseGroupSubtitle: "Circuito de un grupo de mayores por Portugal",
  miscLandCaseGroupChallenge:
    "Inge (72, marcapasos), Werner (75, operación de cadera), Helga (68, diálisis): 22 mayores, 110 medicamentos, 10 días en Portugal.",
  miscLandCaseGroupSolution:
    "Cada viajero llevaba su propio código QR, de modo que un técnico de emergencias podía leer medicamentos y enfermedades sin esperar a localizar a un familiar.",
  miscLandCaseGroupResult2: "Enfermedades legibles sin intérprete",
  miscLandCaseGroupResult3:
    "Ningún familiar necesario para confirmar un medicamento",
  miscLandCaseCruiseTitle: "Un crucero con confianza",
  miscLandCaseCruiseSubtitle: "El viaje de María por el Mediterráneo",
  miscLandCaseCruiseChallenge:
    "María (62) tiene diabetes, alergia a la penicilina y un marcapasos. Crucero de 14 días por Grecia, Italia y Croacia.",
  miscLandCaseCruiseSolution:
    "Pulsera con código QR y sus datos de salud en cinco idiomas, recordatorios de medicación y acceso inmediato para los médicos del barco.",
  miscLandCaseCruiseResult1: "Ninguna toma de medicación olvidada",
  miscLandCaseCruiseResult2:
    "Sin buscar una lista de medicamentos en un puerto extranjero",
  miscLandCaseCruiseResult3:
    "El médico del barco ve al instante su marcapasos y su alergia",
  miscLandCaseFamilyTitle: "Viajar en familia con seguridad",
  miscLandCaseFamilySubtitle: "La familia Müller en Tailandia",
  miscLandCaseFamilyChallenge:
    "Familia de 4 miembros; Emma (8) tiene una alergia al cacahuete potencialmente mortal. 3 semanas en Tailandia y Camboya.",
  miscLandCaseFamilySolution:
    "Un código QR para Emma que indica la alergia y su autoinyector de adrenalina, con sus padres como contactos de emergencia, avisados por correo cuando se lee.",
  miscLandCaseFamilyResult1: "Información importante disponible rápidamente",
  miscLandCaseFamilyResult2: "La alergia figura antes de que nadie pregunte",
  miscLandCaseFamilyResult3:
    "Los padres reciben un correo cuando se lee el código",
  miscLandCaseSoloTitle: "Viajar solo, pero no solo",
  miscLandCaseSoloSubtitle: "La vuelta al mundo de 6 meses de Max",
  miscLandCaseSoloChallenge:
    "Max (26), con diabetes tipo 1, viaja solo por 12 países. Gestión de la insulina, actividades extremas, zonas remotas.",
  miscLandCaseSoloSolution:
    "Datos de salud legibles sin conexión, un código QR impreso en la mochila, su insulina en la lista de medicamentos y aviso a casa cuando se lee el código.",
  miscLandCaseSoloResult1: "Información de emergencia disponible sin conexión",
  miscLandCaseSoloResult2: "Insulina y dosis legibles para un desconocido",
  miscLandCaseSoloResult3:
    "La diabetes se conoce antes de iniciar el tratamiento",
  miscLandCaseSeniorsTitle: "Viajes en grupo más sencillos",
  miscLandCaseSeniorsSubtitle: "Grupo de mayores en Portugal",
  miscLandCaseSeniorsChallenge:
    "22 mayores (de 60 a 78 años) con 110 medicamentos en total. Marcapasos, diálisis, antecedentes de ictus, pérdida auditiva.",
  miscLandCaseSeniorsSolution:
    "Un código QR por viajero, cada uno con medicamentos, enfermedades y contactos de emergencia, además de los hospitales y clínicas cercanos a cada parada.",
  miscLandCaseSeniorsResult2: "110 medicamentos documentados, no memorizados",
  miscLandCaseSeniorsResult3:
    "Los contactos de cada viajero localizables al momento",
  miscLandCasesMore: "Ver más ejemplos de viaje",

  miscLandPricingMonthlyTitle: "Suscripciones mensuales",
  miscLandPricingMonthlyNote:
    "Incluye un plan GRATUITO de por vida. Mejore su plan cuando quiera.",
  miscLandPeriodForever: "para siempre",
  miscLandPeriodMonth: "mes",
  miscLandPeriodOneTrip: "un viaje",
  miscLandSingleAccess: "{days} días de acceso",
  miscLandPlanEmergencyData: "Datos de salud de emergencia",
  miscLandPlanQrAccess: "Acceso mediante código QR",
  miscLandPlanBasicTravel: "Planificación básica de viajes",
  miscLandPlanFiveReminders: "5 recordatorios de medicación/mes",
  miscLandPlanGdprStorage: "Almacenamiento conforme al RGPD",
  miscLandPlanEverythingFree: "Todo lo incluido en FREE",
  miscLandPlanUnlimitedReminders: "Recordatorios ilimitados",
  miscLandPlanTravelReqs: "Requisitos sanitarios de viaje",
  miscLandPlanVaccTracking: "Seguimiento de vacunas",
  miscLandPlanStorage1: "Almacenamiento de documentos (1 GB)",
  miscLandPlanPriority: "Soporte prioritario",
  miscLandPlanOffline: "Acceso sin conexión",
  miscLandPlanEverythingPlus: "Todo lo incluido en PLUS",
  miscLandPlanCompanions: "Acompañantes en un viaje",
  miscLandPlanNearby: "Hospitales, farmacias y clínicas cerca de su destino",
  miscLandPlanDoctorSummary:
    "Resumen de salud del viaje enviado a su propio médico",
  miscLandPlanAdviceWatch:
    "Seguimiento de los cambios en las recomendaciones sanitarias de sus destinos",
  miscLandPlanStorage10: "Almacenamiento de documentos (10 GB)",
  miscLandPlanAllPlus: "Todas las funciones de PLUS",
  miscLandPlanAllPremium: "Todas las funciones de PREMIUM",
  miscLandChoosePlus: "Elegir PLUS",
  miscLandChoosePremium: "Elegir PREMIUM",
  miscLandBuySingleTrip: "Comprar viaje único",
  miscLandBadgeMostPopular: "El más popular",
  miscLandBadgeBestOneTrip: "Ideal para un viaje",
  miscLandSingleTitle: "Planes para un solo viaje",
  miscLandSingleNote:
    "Pago único. Acceso completo durante 90 días, sin suscripción.",

  miscLandSignIn: "Iniciar sesión",
  miscLandCtaNote:
    "No se requiere tarjeta de crédito • Plan GRATUITO de por vida • Mejore su plan cuando quiera",

  miscLandLogoAlt: "Logotipo de HealthPass",
  miscLandFooterTagline:
    "Sus datos de salud, siempre con usted. Viaje seguro por todo el mundo.",
  miscLandFooterProduct: "Producto",
  miscLandFooterFeatures: "Funciones",
  miscLandFooterPricing: "Precios",
  miscLandFooterSecurity: "Seguridad",
  miscLandFooterFaq: "Preguntas frecuentes",
  miscLandFooterRecordsApp: "App de historial médico para viajes",
  miscLandFooterCompany: "Empresa",
  miscLandFooterBlog: "Blog",
  miscLandFooterContact: "Contacto",
  miscLandFooterLegal: "Legal",
  miscLandFooterPrivacy: "Política de privacidad",
  miscLandFooterTerms: "Condiciones del servicio",
  miscLandFooterImprint: "Aviso legal",
  miscLandFooterCopyright:
    "© {year} HealthPass. Todos los derechos reservados. Hecho para viajeros.",
};

const pt: AppPagesLandingStrings = {
  miscLandSeoTitle:
    "Passaporte de saúde de viagem – O seu historial médico e cartão de emergência no estrangeiro",
  miscLandSeoDescription:
    "O HealthPass é um passaporte de saúde de viagem: as suas doenças, medicamentos, alergias, vacinas e contactos de emergência num só lugar, com um cartão de emergência que um médico no estrangeiro lê em cinco idiomas. Grátis para começar, sem loja de aplicações.",

  miscLandHeroLanguagesBadge: "Disponível em 5 idiomas",
  miscLandHeroTravelerHeadline:
    "O seu passaporte de saúde de viagem: historial médico e cartão de emergência para cada viagem",
  miscLandHeroTravelerSubheadline:
    "O HealthPass guarda as suas doenças, medicamentos, alergias, vacinas e contactos de emergência num só lugar e reúne o essencial num cartão de emergência. Um médico no estrangeiro digitaliza o código QR e lê-o em inglês, alemão, francês, espanhol ou português, sem aplicação e sem início de sessão. O cartão é grátis e o HealthPass funciona no navegador, por isso não há nada a descarregar de uma loja de aplicações.",
  miscLandFaqTitle: "Perguntas dos viajantes",
  miscLandFaqQ1: "O que é um passaporte de saúde de viagem?",
  miscLandFaqA1:
    "Mantém num só lugar a informação médica de que precisa numa viagem: doenças, medicamentos, alergias, vacinas e contactos de emergência, bem como os documentos de saúde que o seu destino exige. O HealthPass é um, e o seu cartão de emergência pode ser lido no estrangeiro sem aplicação.",
  miscLandFaqQ2: "Um médico no estrangeiro pode ler a minha informação médica?",
  miscLandFaqA2:
    "Sim. O seu cartão de emergência tem um código QR. Ao digitalizá-lo, o cartão abre num navegador, em inglês, alemão, francês, espanhol ou português, sem aplicação e sem início de sessão. É o utilizador que decide o que o cartão mostra.",
  miscLandFaqQ3: "Preciso de descarregar uma aplicação?",
  miscLandFaqA3:
    "Não. O HealthPass funciona no navegador de qualquer telemóvel ou computador e pode ser adicionado ao ecrã inicial como uma aplicação. Não está na App Store nem no Google Play.",
  miscLandFaqQ4: "Quanto custa o HealthPass?",
  miscLandFaqA4:
    "O cartão de emergência e o planeamento básico de viagens são grátis para sempre. O PLUS custa {plus} por mês e o PREMIUM {premium} por mês; para uma única viagem, o PLUS custa {plusTrip} e o PREMIUM {premiumTrip} durante 90 dias.",
  miscLandHeroNomadHeadline:
    "Apoio de saúde para viagens flexíveis e frequentes",
  miscLandHeroNomadSubheadline:
    "Mantenha à mão as informações de saúde importantes, os dados da medicação e o acesso de emergência enquanto se desloca entre destinos e fusos horários.",
  miscLandHeroCorporateHeadline:
    "Apoio de saúde digital para viagens de negócios e de trabalho",
  miscLandHeroCorporateSubheadline:
    "Prepare as informações de saúde importantes num só lugar para as viagens, o check-in e as situações urgentes durante a deslocação.",
  miscLandHeroFamilyHeadline:
    "Apoio para famílias, acompanhantes e viagens em grupo",
  miscLandHeroFamilySubheadline:
    "Tenha prontos os dados médicos mais importantes das crianças, dos familiares mais velhos e do planeamento conjunto da viagem.",
  miscLandHeroChronicHeadline: "Apoio para viajantes com problemas de saúde",
  miscLandHeroChronicSubheadline:
    "Organize as informações médicas importantes, a medicação e o acesso de emergência antes da viagem para viajar com mais confiança.",
  miscLandCtaCreateFreeAccount: "Crie a sua conta gratuita",
  miscLandCtaRequestDemo: "Pedir uma demonstração",
  miscLandFreeLifetimePlan: "Plano GRATUITO vitalício",

  miscLandAudienceHealthMinded: "Viajantes atentos à saúde",
  miscLandAudienceHealthIssues: "Viajantes com problemas de saúde",
  miscLandAudienceOlder: "Viajantes mais velhos",
  miscLandAudienceDisabled: "Viajantes com deficiência",
  miscLandAudienceActivities: "Viajantes que planeiam atividades especiais",
  miscLandAudiencePlaneCruise: "Viajantes de avião ou de cruzeiro",

  miscLandMockEmergencyData: "Dados de emergência",
  miscLandMockAlwaysAccessible: "Sempre acessíveis",
  miscLandMockScan: "Ler",
  miscLandMockBloodType: "Grupo sanguíneo: A+",
  miscLandMockVerified: "Verificado",
  miscLandMockAllergies: "Alergias: penicilina",
  miscLandMockSevere: "Grave",
  miscLandMockUpcomingTrip: "Próxima viagem",
  miscLandMockTripDestination: "Banguecoque, Tailândia",
  miscLandMockMedReminder: "Lembrete de medicação",
  miscLandMockDailyAt: "Diariamente às {time}",
  miscLandMockEncrypted: "Encriptado",

  miscLandTrustPrivacy: "Focado na privacidade",
  miscLandTrustSecureAccess: "Acesso digital seguro",
  miscLandTrustManySituations: "Útil em muitas situações de viagem",
  miscLandTrustInternational: "Pensado para viagens internacionais",

  miscLandCardBadge: "Cartão HEALTHPASS",
  miscLandCardTitle: "Um cartão. Quatro ações essenciais em viagem.",
  miscLandCardIntro:
    "O cartão HEALTHPASS mantém as informações de saúde importantes prontas para o check-in, a comunicação de emergência, o acesso médico e a sua carteira digital.",
  miscLandCardPoint1:
    "Identidade digital e acesso de saúde prontos para viajar, num só lugar",
  miscLandCardPoint2:
    "Partilha rápida de informações importantes quando é preciso ajuda",
  miscLandCardPoint3:
    "Pensado para viagens internacionais, cruzeiros e viagens de avião",
  miscLandCardActionCheckIn: "Check-in",
  miscLandCardActionEmergency: "Emergência",
  miscLandCardActionMedAccess: "Acesso médico",
  miscLandCardActionWallet: "Wallet",

  miscLandChronicTitle: "Vive com uma doença crónica?",
  miscLandChronicIntro:
    "Não deixe que os problemas de saúde o impeçam de descobrir o mundo. O HEALTHPASS apoia uma vasta gama de doenças crónicas e necessidades de saúde relacionadas com viagens.",
  miscLandChronicDiabetesTitle: "Diabetes (tipo 1 e 2)",
  miscLandChronicDiabetes1: "Insulina e outros medicamentos numa só lista",
  miscLandChronicDiabetes2: "Glicemia e valores analíticos que regista por si",
  miscLandChronicDiabetes3: "Lembretes de medicação",
  miscLandChronicDiabetes4: "Diabetes e alergias no cartão de emergência",
  miscLandChronicIbdTitle: "DII (Crohn e colite)",
  miscLandChronicIbd1: "Biológicos e receitas na sua lista de medicamentos",
  miscLandChronicIbd2:
    "Cartas do médico guardadas como documentos que pode mostrar",
  miscLandChronicIbd3: "Farmácias e clínicas perto do seu destino",
  miscLandChronicIbd4: "Diagnóstico e medicação no cartão de emergência",
  miscLandChronicNeuroTitle: "Neurológicas (EM, epilepsia)",
  miscLandChronicNeuro1: "Cartão de emergência legível em cinco idiomas",
  miscLandChronicNeuro2:
    "Antiepiléticos e doses visíveis para as equipas de socorro",
  miscLandChronicNeuro3:
    "Contactos de emergência que as equipas de socorro podem ligar",
  miscLandChronicHospitalsNearby: "Hospitais e clínicas perto do seu destino",
  miscLandChronicCardiacTitle: "Doenças cardíacas",
  miscLandChronicCardiac1: "Cartão de pacemaker ou CDI guardado como documento",
  miscLandChronicCardiac2:
    "Anticoagulantes na sua lista de medicamentos e lembretes",
  miscLandChronicCardiac3: "Doença, alergias e contactos no cartão QR",
  miscLandChronicAutoimmuneTitle: "Autoimunes (artrite reumatoide, lúpus)",
  miscLandChronicAutoimmune1: "Imunossupressores na sua lista de medicamentos",
  miscLandChronicAutoimmune2:
    "Boletim de vacinas à mão quando um médico o pedir",
  miscLandChronicAutoimmune3: "Requisitos de saúde do destino antes de partir",
  miscLandChronicAutoimmune4: "Clínicas e farmácias perto do seu destino",
  miscLandChronicKidneyTitle: "Doença renal",
  miscLandChronicKidney1:
    "Historial de diálise e valores analíticos nos seus registos",
  miscLandChronicKidney2: "Medicamentos e doses numa só lista",
  miscLandChronicKidney3: "Doença renal no cartão de emergência",
  miscLandConfidenceTitle: "Viaje com confiança",
  miscLandConfidenceText:
    "Pode viajar sem medo, mesmo que viva com problemas de saúde. Descubra como com o HEALTHPASS. Já não precisa de ficar em casa.",
  miscLandConfidencePoint1:
    "Informações de saúde importantes prontas quando são precisas",
  miscLandConfidencePoint2: "Melhor preparação para diferentes tipos de viagem",
  miscLandConfidencePoint3:
    "Comunicação mais rápida e clara num incidente médico",
  miscLandConfidencePoint4: "Uma experiência de viagem mais segura e fluida",
  miscLandConfidenceQuote:
    "Viajar pode voltar a ser possível quando a informação certa está disponível no momento certo.",
  miscLandStatsMillions: "Milhões de pessoas em todo o mundo",
  miscLandStatsLiveWith: "vivem com doenças crónicas.",
  miscLandStatsAvoid: "evitam viajar por medo.",
  miscLandStatsReconsider: "Com o HEALTHPASS, viajar volta a ser uma opção.",
  miscLandStartJourney: "Comece hoje a sua viagem",

  miscLandWhyTitle: "Porque é que o HEALTHPASS é importante",
  miscLandWhyIntro:
    "O HEALTHPASS ajuda-o a compreender os riscos de saúde a que pode estar exposto durante a viagem e apoia-o na preparação.",
  miscLandWhyPrepTitle: "Melhor preparação antes e durante a viagem",
  miscLandWhyPrepText1:
    "O HEALTHPASS ajuda-o a compreender os riscos de saúde que podem estar relacionados com o seu destino, o tipo de viagem ou as atividades planeadas. Oferece serviços que podem ajudar a reduzir esses riscos.",
  miscLandWhyPrepText2:
    "Em caso de incidente médico, a informação certa pode ser disponibilizada mais depressa e com mais clareza, apoiando uma decisão de tratamento melhor e mais adequada.",
  miscLandWhySeamlessTitle: "Uma experiência de viagem digital mais fluida",
  miscLandWhySeamlessText1:
    "A sua interação com operadores turísticos ou outros parceiros de viagem que pedem informações de saúde torna-se uma experiência digital mais fluida.",
  miscLandWhySeamlessText2:
    "Sem formulários em papel, sem voltar a introduzir as mesmas informações em cada viagem e com menos atritos no check-in ou nas fronteiras.",
  miscLandWhyBanner:
    "Ajuda a tornar as viagens mais seguras e toda a experiência mais fluida.",

  miscLandSupportTitle: "Viaje de avião ou de cruzeiro mais bem preparado",
  miscLandSupportIntro:
    "Mantenha as informações de saúde essenciais prontas para o check-in, as passagens de fronteira e as situações médicas urgentes durante a viagem.",
  miscLandSupportRiskTitle: "Consciência dos riscos",
  miscLandSupportRiskText:
    "Compreenda os riscos de saúde relevantes antes da partida.",
  miscLandSupportFasterTitle: "Ajuda mais rápida",
  miscLandSupportFasterText:
    "Partilhe mais depressa as informações médicas essenciais quando é importante.",
  miscLandSupportRepetitionTitle: "Menos repetição",
  miscLandSupportRepetitionText:
    "Evite introduzir os mesmos dados de saúde em cada viagem.",
  miscLandSupportConfidenceTitle: "Mais confiança",
  miscLandSupportConfidenceText:
    "Viaje com mais tranquilidade, da partida à chegada.",
  miscLandDemoTitle: "Marque uma demonstração ao vivo",
  miscLandDemoText:
    "Veja como o HEALTHPASS pode contribuir para viagens mais seguras e fluidas.",
  miscLandDemoCta: "Marcar uma demonstração",

  miscLandIssuesTitle: "Para viajantes com problemas de saúde",
  miscLandIssuesIntro:
    "O HEALTHPASS pode ajudá-lo a preparar as informações importantes antes da viagem, para viajar com mais confiança e tranquilidade.",
  miscLandIssuesFearTitle: "Viajar sem medo",
  miscLandIssuesFearSubtitle:
    "Melhor preparação para situações médicas e de viagem",
  miscLandIssuesText1:
    "O HEALTHPASS ajuda a organizar as informações essenciais sobre doenças crónicas, deficiências, medicamentos, alergias e outros aspetos de saúde que podem ser importantes durante uma viagem.",
  miscLandIssuesText2:
    "Isto pode apoiar uma comunicação mais rápida e uma experiência digital mais fluida quando são pedidas informações de saúde.",
  miscLandIssuesHelpTitle: "Em que o HEALTHPASS pode ajudar:",
  miscLandIssuesHelp1: "Informações importantes prontas quando são precisas",
  miscLandIssuesHelp2:
    "Melhor preparação para os riscos e as atividades da viagem",
  miscLandIssuesHelp3:
    "Partilha mais fluida no check-in ou em pedidos de ajuda",
  miscLandIssuesHelp4: "Mais confiança antes e durante a viagem",
  miscLandIssuesCta: "Comece grátis e faça o upgrade quando viajar",
  miscLandIssuesCtaNote:
    "O plano gratuito mantém o seu cartão de emergência. O PLUS acrescenta os requisitos de saúde do destino e lembretes de medicação ilimitados por {price}/mês.",

  miscLandFeatureEmergencyTitle: "Dados de saúde de emergência",
  miscLandFeatureEmergencyDesc:
    "As suas informações de saúde críticas disponíveis 24 horas por dia através de um código QR seguro",
  miscLandFeatureGdprTitle: "Concebido para o RGPD",
  miscLandFeatureGdprDesc:
    "Encriptado em trânsito e em repouso, cada acesso a um registo fica registado",
  miscLandFeatureTravelTitle: "Planeamento de viagens",
  miscLandFeatureTravelDesc:
    "Requisitos de saúde específicos de cada destino e lembretes de vacinação",
  miscLandFeatureMedsTitle: "Lembretes de medicação",
  miscLandFeatureMedsDesc:
    "Nunca mais falhe uma dose graças ao acompanhamento inteligente da medicação",
  miscLandFeatureLanguagesTitle: "Cinco idiomas",
  miscLandFeatureLanguagesDesc:
    "A aplicação e a vista de emergência estão disponíveis em inglês, alemão, francês, espanhol e português.",
  miscLandFeatureMobileTitle: "Pronto para o telemóvel",
  miscLandFeatureMobileDesc:
    "Aceda aos seus dados de saúde a qualquer hora, em qualquer lugar e em qualquer dispositivo",

  miscLandStep1Title: "Criar conta",
  miscLandStep1Desc: "Registe-se em 2 minutos apenas com o seu e-mail",
  miscLandStep2Title: "Adicionar dados de saúde",
  miscLandStep2Desc: "Introduza as suas alergias, medicamentos e doenças",
  miscLandStep3Title: "Viajar em segurança",
  miscLandStep3Desc: "Obtenha o seu código QR e viaje com tranquilidade",

  miscLandHubTitle: "Viaja com uma doença crónica?",
  miscLandHubIntro:
    "O HEALTHPASS apoia uma vasta gama de doenças crónicas com informações prontas para viajar, apoio multilingue e ferramentas de acesso de emergência. Não precisa de ficar em casa.",
  miscLandHubDiabetes1: "Lista de insulina e medicamentos",
  miscLandHubDiabetes2: "Registo de glicemia",
  miscLandHubDiabetes3: "Lembrete para cada dose",
  miscLandHubHeartTitle: "Doença cardíaca",
  miscLandHubHeart1: "Documentos do pacemaker",
  miscLandHubHeart2: "Lembretes de anticoagulantes",
  miscLandHubConditionOnQr: "Doença indicada no cartão QR",
  miscLandHubCopdTitle: "DPOC e asma",
  miscLandHubCopd1: "Formulários de oxigénio como documentos",
  miscLandHubCopd2: "Lembretes do inalador",
  miscLandHubCopd3: "Farmácias no seu destino",
  miscLandHubCrohnsTitle: "Crohn e colite",
  miscLandHubCrohns1: "Biológicos na sua lista de medicamentos",
  miscLandHubCrohns2: "Cartas guardadas como documentos",
  miscLandHubClinics: "Clínicas no seu destino",
  miscLandHubEpilepsyTitle: "Epilepsia e convulsões",
  miscLandHubEpilepsy1: "Cartão de emergência em 5 idiomas",
  miscLandHubEpilepsy2: "Antiepiléticos e doses",
  miscLandHubHospitals: "Hospitais no seu destino",
  miscLandHubKidney1: "Historial de diálise e análises",
  miscLandHubKidney2: "Medicamentos e doses",
  miscLandHubRaTitle: "Artrite reumatoide",
  miscLandHubRa1: "Lista de imunossupressores",
  miscLandHubRa2: "Boletim de vacinas",
  miscLandHubMsTitle: "Esclerose múltipla",
  miscLandHubMs1: "Medicamentos e perfusões",
  miscLandHubReadGuide: "Ler o guia de viagem",
  miscLandHubMoreGuides: "Mais guias de saúde em viagem (em inglês)",
  miscLandHubCta: "Comece com o plano GRATUITO vitalício",
  miscLandHubNote:
    "Não é necessário cartão de crédito · Funciona com o seu prestador de cuidados de saúde atual",

  miscLandCasesTitle: "Exemplos de situações de viagem",
  miscLandCasesIntro:
    "Exemplos ilustrativos de como o HEALTHPASS pode contribuir para viagens mais seguras e fluidas.",
  miscLandChallenge: "Desafio:",
  miscLandSolution: "Solução:",
  miscLandCaseAllReturned: "Os 22 regressaram em segurança",
  miscLandCaseGroupTitle: "22 viajantes, 22 códigos QR",
  miscLandCaseGroupSubtitle: "Circuito de um grupo de seniores em Portugal",
  miscLandCaseGroupChallenge:
    "Inge (72, pacemaker), Werner (75, cirurgia à anca), Helga (68, diálise) – 22 seniores, 110 medicamentos, 10 dias em Portugal.",
  miscLandCaseGroupSolution:
    "Cada viajante levava o seu próprio código QR, para que um paramédico pudesse ler os medicamentos e as doenças sem esperar que um familiar fosse contactado.",
  miscLandCaseGroupResult2: "Doenças legíveis sem tradutor",
  miscLandCaseGroupResult3:
    "Nenhum familiar necessário para confirmar um medicamento",
  miscLandCaseCruiseTitle: "Cruzeiro com confiança",
  miscLandCaseCruiseSubtitle: "A viagem de Maria pelo Mediterrâneo",
  miscLandCaseCruiseChallenge:
    "Maria (62) tem diabetes, alergia à penicilina e um pacemaker. Cruzeiro de 14 dias pela Grécia, Itália e Croácia.",
  miscLandCaseCruiseSolution:
    "Pulseira com código QR e os seus dados de saúde em cinco idiomas, lembretes de medicação e acesso imediato para os médicos de bordo.",
  miscLandCaseCruiseResult1: "Nenhuma toma de medicação esquecida",
  miscLandCaseCruiseResult2:
    "Sem procurar uma lista de medicamentos num porto estrangeiro",
  miscLandCaseCruiseResult3:
    "O médico de bordo vê de imediato o pacemaker e a alergia",
  miscLandCaseFamilyTitle: "Viajar em família em segurança",
  miscLandCaseFamilySubtitle: "A família Müller na Tailândia",
  miscLandCaseFamilyChallenge:
    "Família de 4 pessoas; Emma (8) tem uma alergia ao amendoim potencialmente fatal. 3 semanas na Tailândia e no Camboja.",
  miscLandCaseFamilySolution:
    "Um código QR para a Emma que indica a alergia e a sua caneta de adrenalina, com os pais como contactos de emergência, avisados por e-mail quando é lido.",
  miscLandCaseFamilyResult1: "Informações importantes disponíveis rapidamente",
  miscLandCaseFamilyResult2: "A alergia é indicada antes de alguém perguntar",
  miscLandCaseFamilyResult3: "Os pais recebem um e-mail quando o código é lido",
  miscLandCaseSoloTitle: "Viajar sozinho, mas não só",
  miscLandCaseSoloSubtitle: "A volta ao mundo de 6 meses do Max",
  miscLandCaseSoloChallenge:
    "Max (26), com diabetes tipo 1, viaja sozinho por 12 países. Gestão da insulina, atividades radicais, zonas remotas.",
  miscLandCaseSoloSolution:
    "Dados de saúde legíveis offline, um código QR impresso na mochila, a insulina na lista de medicamentos e aviso para casa quando o código é lido.",
  miscLandCaseSoloResult1: "Informações de emergência disponíveis offline",
  miscLandCaseSoloResult2: "Insulina e doses legíveis para um desconhecido",
  miscLandCaseSoloResult3:
    "A diabetes é conhecida antes do início do tratamento",
  miscLandCaseSeniorsTitle: "Viagens em grupo simplificadas",
  miscLandCaseSeniorsSubtitle: "Grupo de seniores em Portugal",
  miscLandCaseSeniorsChallenge:
    "22 seniores (60 a 78 anos) com 110 medicamentos no total. Pacemakers, diálise, antecedentes de AVC, perda auditiva.",
  miscLandCaseSeniorsSolution:
    "Um código QR por viajante, cada um com medicamentos, doenças e contactos de emergência, e os hospitais e clínicas perto de cada paragem.",
  miscLandCaseSeniorsResult2: "110 medicamentos documentados, não memorizados",
  miscLandCaseSeniorsResult3:
    "Os contactos de cada viajante contactáveis no momento",
  miscLandCasesMore: "Ver mais exemplos de viagem",

  miscLandPricingMonthlyTitle: "Subscrições mensais",
  miscLandPricingMonthlyNote:
    "Inclui um plano GRATUITO vitalício. Faça o upgrade quando quiser.",
  miscLandPeriodForever: "para sempre",
  miscLandPeriodMonth: "mês",
  miscLandPeriodOneTrip: "uma viagem",
  miscLandSingleAccess: "{days} dias de acesso",
  miscLandPlanEmergencyData: "Dados de saúde de emergência",
  miscLandPlanQrAccess: "Acesso por código QR",
  miscLandPlanBasicTravel: "Planeamento básico de viagens",
  miscLandPlanFiveReminders: "5 lembretes de medicação/mês",
  miscLandPlanGdprStorage: "Armazenamento conforme o RGPD",
  miscLandPlanEverythingFree: "Tudo o que o FREE inclui",
  miscLandPlanUnlimitedReminders: "Lembretes ilimitados",
  miscLandPlanTravelReqs: "Requisitos de saúde para viagens",
  miscLandPlanVaccTracking: "Acompanhamento de vacinas",
  miscLandPlanStorage1: "Armazenamento de documentos (1 GB)",
  miscLandPlanPriority: "Suporte prioritário",
  miscLandPlanOffline: "Acesso offline",
  miscLandPlanEverythingPlus: "Tudo o que o PLUS inclui",
  miscLandPlanCompanions: "Acompanhantes numa viagem",
  miscLandPlanNearby: "Hospitais, farmácias e clínicas perto do seu destino",
  miscLandPlanDoctorSummary:
    "Resumo de saúde da viagem enviado ao seu próprio médico",
  miscLandPlanAdviceWatch:
    "Acompanhamento das alterações às recomendações de saúde para os seus destinos",
  miscLandPlanStorage10: "Armazenamento de documentos (10 GB)",
  miscLandPlanAllPlus: "Todas as funcionalidades PLUS",
  miscLandPlanAllPremium: "Todas as funcionalidades PREMIUM",
  miscLandChoosePlus: "Escolher PLUS",
  miscLandChoosePremium: "Escolher PREMIUM",
  miscLandBuySingleTrip: "Comprar viagem única",
  miscLandBadgeMostPopular: "O mais popular",
  miscLandBadgeBestOneTrip: "Ideal para uma viagem",
  miscLandSingleTitle: "Planos para uma só viagem",
  miscLandSingleNote:
    "Pagamento único. Acesso total durante 90 dias – sem subscrição.",

  miscLandSignIn: "Iniciar sessão",
  miscLandCtaNote:
    "Não é necessário cartão de crédito • Plano GRATUITO vitalício • Faça o upgrade quando quiser",

  miscLandLogoAlt: "Logótipo HealthPass",
  miscLandFooterTagline:
    "Os seus dados de saúde, sempre consigo. Viaje em segurança por todo o mundo.",
  miscLandFooterProduct: "Produto",
  miscLandFooterFeatures: "Funcionalidades",
  miscLandFooterPricing: "Preços",
  miscLandFooterSecurity: "Segurança",
  miscLandFooterFaq: "Perguntas frequentes",
  miscLandFooterRecordsApp: "App de histórico médico para viagens",
  miscLandFooterCompany: "Empresa",
  miscLandFooterBlog: "Blogue",
  miscLandFooterContact: "Contacto",
  miscLandFooterLegal: "Informação legal",
  miscLandFooterPrivacy: "Política de privacidade",
  miscLandFooterTerms: "Termos de serviço",
  miscLandFooterImprint: "Ficha técnica",
  miscLandFooterCopyright:
    "© {year} HealthPass. Todos os direitos reservados. Feito para viajantes.",
};

export const appPagesLandingStrings: Record<Language, AppPagesLandingStrings> =
  {
    en,
    de,
    fr,
    es,
    pt,
  };
